<template>
    <div id="page-pull-requests" data-page="pull-requests">
        <div class="pr-management-page">

            <b-row class="my-2 align-items-center" v-if="hasRepoSource && hasBacklogConnection">
                <b-col cols="12" class="d-flex justify-content-center">
                    <picker-sprints require-repo connection-context="repo" @sprint-picked="onSprintPicked" @sprints-loaded="onSprintsLoaded"></picker-sprints>
                </b-col>
            </b-row>

            <!-- Missing Repository Source Warning Card -->
            <card-missing-source
                v-if="isMissingRepoVisible"
                source-type="repo"
                feature-name="Pull Requests"
                :project="activeProject"
            />

            <!-- Service Unavailable Placeholder -->
            <div v-else-if="isServiceUnavailable" class="text-center py-5 text-muted">
                <i class="bi bi-cloud-slash fa-2x mb-2 d-block text-warning"></i>
                <div class="font-weight-bold mb-1">Service is currently unreachable</div>
                <span class="small">Pull requests will be loaded automatically once the service is restored.</span>
            </div>

            <!-- PR Content Area -->
            <template v-else>
                <!-- Header Banner / Stats -->
                <grid-header-banner
                    title="Pull Requests"
                    subtitle="Code review queues, quality validation checks, and branch merge tracking"
                    icon="icon-azure-pr"
                    icon-color="text-primary"
                    :stats="prStats"
                />

                <!-- PR Grid Area with Reserved Vertical Space -->
                <b-overlay :show="loadingPullRequests" rounded="lg" opacity="0.6" class="pr-grid-overlay" :class="{ 'no-sprints': !hasVisibleSprints }">
                <template #overlay>
                    <load-spinner text="Loading pull requests..." />
                </template>
                <grid-shell
                    v-if="isGridVisible"
                    class="pr-items-grid"
                    auto-fit-height
                    :columns="visibleGridColumns"
                    :custom-columns-handler="openColumnSettings"
                    columns-button-test-id="btn-configure-columns"
                    :loading="loadingPullRequests"
                    :empty="!loadingPullRequests && !visibleGroups.length"
                    :has-active-filters="isAnyPrFilterActive"
                    :empty-title="prEmptyTitle"
                    :empty-message="prEmptyMessage"
                    :empty-suggestions="prFilterSuggestions"
                    :empty-actions="prEmptyActions"
                    @reset-filters="resetAllPrFilters"
                    @empty-action="onEmptyAction"
                >
                    <template #filters>
                        <!-- Server-Side Filter Section -->
                        <div class="filter-toolbar-label mr-2">
                            <i class="bi bi-funnel mr-1"></i><strong>Filters:</strong>
                        </div>

                        <!-- Author filter chip -->
                        <filter-author-dropdown
                            v-model="selectedAuthors"
                            :project="activeProject"
                            :show-all-option="true"
                            default-key="me"
                            :project-id="activeProject ? activeProject.id : null"
                            class="mr-2"
                            data-test="filter-chip-author"
                            :disabled="loadingPullRequests"
                        />

                        <!-- Status filter chip -->
                        <b-dropdown size="sm" :variant="isStatusFilterActive ? 'primary' : 'outline-secondary'" no-caret
                            class="grid-filter-chip mr-2"
                            data-test="filter-chip-status">
                            <template #button-content>
                                <i :class="isStatusFilterActive ? 'bi bi-funnel-fill mr-1' : 'bi bi-funnel mr-1'"></i>
                                <span class="font-weight-bold mr-1">Status:</span>
                                <span>{{ statusFilterText }}</span>
                                <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                            </template>
                            <b-dropdown-form class="filter-chip-dropdown-form p-2">
                                <div class="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                                    <small class="font-weight-bold">Filter by Status</small>
                                    <div>
                                        <b-button size="sm" variant="link" class="p-0 text-decoration-none"
                                            @click="selectAllStatuses">All</b-button>
                                    </div>
                                </div>
                                <div class="filter-options-list">
                                    <b-form-checkbox
                                        v-for="opt in statusOptions"
                                        :key="opt"
                                        :checked="statusFilter.includes(opt)"
                                        :disabled="statusFilter.length <= 1 && statusFilter.includes(opt)"
                                        :title="statusFilter.length <= 1 && statusFilter.includes(opt) ? 'At least one status must be selected' : ''"
                                        class="m-0 compact-checkbox"
                                        @change="toggleStatusFilter(opt, $event)"
                                    >
                                        {{ opt }}
                                    </b-form-checkbox>
                                </div>
                            </b-dropdown-form>
                        </b-dropdown>

                        <!-- Reviewers 2-option DDL chip -->
                        <!-- Reviewers filter chip (Me, Team, or specific person, plus All) -->
                        <filter-author-dropdown
                            v-if="true"
                            v-model="selectedReviewers"
                            label="Reviewers"
                            :project-id="activeProject ? activeProject.id : null"
                            :project="activeProject"
                            :show-all-option="true"
                            default-key="all"
                            test-prefix="filter-reviewer"
                            class="mr-2"
                            data-test="filter-chip-reviewers"
                            :disabled="loadingPullRequests"
                        />
                        <!-- Legacy Reviewers 2-option DDL chip hidden -->
                        <b-dropdown v-if="false" size="sm" :variant="isReviewersFilterActive ? 'primary' : 'outline-secondary'" no-caret
                            class="grid-filter-chip mr-2"
                            data-test="filter-chip-reviewers">
                            <template #button-content>
                                <i :class="isReviewersFilterActive ? 'bi bi-funnel-fill mr-1' : 'bi bi-people mr-1'"></i>
                                <span class="font-weight-bold mr-1">Reviewers:</span>
                                <span>{{ showOptionalReviewers ? 'All Reviewers' : 'Required Only' }}</span>
                                <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                            </template>
                            <b-dropdown-item-button :active="!showOptionalReviewers" @click="showOptionalReviewers = false">
                                <i class="bi bi-shield-fill-check mr-2"></i>Required Only
                            </b-dropdown-item-button>
                            <b-dropdown-item-button :active="showOptionalReviewers" @click="showOptionalReviewers = true">
                                <i class="bi bi-people mr-2"></i>All Reviewers
                            </b-dropdown-item-button>
                        </b-dropdown>

                        <!-- Work Items 2-option DDL chip (only relevant when backlog is connected) -->
                        <b-dropdown v-if="hasBacklogConnection" size="sm" :variant="isWorkItemsFilterActive ? 'primary' : 'outline-secondary'" no-caret
                            class="grid-filter-chip mr-2"
                            data-test="filter-chip-workitems">
                            <template #button-content>
                                <i :class="isWorkItemsFilterActive ? 'bi bi-funnel-fill mr-1' : 'bi bi-card-checklist mr-1'"></i>
                                <span class="font-weight-bold mr-1">Work Items:</span>
                                <span>{{ showUnassigned ? 'All Work Items' : 'PRs with Items' }}</span>
                                <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                            </template>
                            <b-dropdown-item-button :active="!showUnassigned" @click="showUnassigned = false">
                                <i class="bi bi-card-checklist mr-2"></i>PRs with Items
                            </b-dropdown-item-button>
                            <b-dropdown-item-button :active="showUnassigned" @click="showUnassigned = true">
                                <i class="bi bi-collection mr-2"></i>All Work Items
                            </b-dropdown-item-button>
                        </b-dropdown>

                        <!-- Action Buttons aligned to the right -->
                        <div class="filter-toolbar-actions ml-auto d-flex align-items-center">
                            <!-- Apply Button -->
                            <b-button size="sm" variant="primary" class="mr-2"
                                :class="{ 'btn-apply-pulse': hasUnappliedFilters && !loadingPullRequests }"
                                @click="applyPrFilters" :disabled="loadingPullRequests || isStatusFilterInvalid"
                                data-test="btn-apply-filters"
                                :data-dirty="hasUnappliedFilters ? 'true' : 'false'"
                                :data-state="loadingPullRequests ? 'loading' : 'idle'"
                                title="Apply filters and reload PRs from server">
                                <i class="bi bi-check-circle mr-1"></i>Apply
                            </b-button>

                            <!-- Reset to Defaults button -->
                            <b-button size="sm" variant="outline-secondary"
                                @click="resetAllPrFilters" :disabled="loadingPullRequests"
                                data-test="btn-reset-filters"
                                title="Reset all PR filters to defaults">
                                <i class="bi bi-arrow-counterclockwise mr-1"></i>Reset to Defaults
                            </b-button>
                        </div>

                    </template>
                    <template #view>
                        <!-- Grouping chip (2 options: No Grouping, By Repo) -->
                        <b-dropdown size="sm" :variant="groupMode !== 'none' ? 'primary' : 'outline-secondary'"
                            no-caret class="grid-filter-chip mr-2"
                            data-test="filter-chip-grouping">
                            <template #button-content>
                                <i class="bi bi-folder2-open mr-1"></i>
                                <span class="font-weight-bold mr-1">Grouping:</span>
                                <span>{{ groupModeLabel }}</span>
                                <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                            </template>
                            <b-dropdown-item-button :active="groupMode === 'none'" @click="groupMode = 'none'" data-test="grouping-option-none">
                                <i class="bi bi-list-ul mr-2"></i>No Grouping
                            </b-dropdown-item-button>
                            <b-dropdown-item-button :active="groupMode === 'repo'" @click="groupMode = 'repo'" data-test="grouping-option-repo">
                                <i class="icon-azure-pr mr-2"></i>By Repo
                            </b-dropdown-item-button>
                        </b-dropdown>
                        <b-button
                            v-if="!prFeatureSettings || prFeatureSettings.enabled !== false"
                            size="sm"
                            :variant="onlyAttentionPrs ? 'warning' : 'outline-secondary'"
                            class="grid-filter-chip mr-2 d-inline-flex align-items-center"
                            :class="{ 'pr-attention-chip-active': onlyAttentionPrs }"
                            data-test="btn-filter-pr-attention"
                            :aria-pressed="onlyAttentionPrs ? 'true' : 'false'"
                            v-b-tooltip.hover.top.html="attentionFilterTooltipHtml"
                            @click="onlyAttentionPrs = !onlyAttentionPrs">
                            <i class="bi bi-lightning-charge-fill mr-1" :class="onlyAttentionPrs ? 'text-dark' : 'text-warning'"></i>
                            <span class="font-weight-bold">Needs Attention</span>
                            <span v-if="attentionPrCount > 0" class="badge ml-1" :class="onlyAttentionPrs ? 'badge-dark' : 'badge-warning text-dark'" data-test="badge-pr-attention-count">{{ attentionPrCount }}</span>
                        </b-button>
                        <b-input-group size="sm" class="mr-2" style="width: 210px;">
                            <b-input-group-prepend is-text>
                                <i class="bi bi-search" :class="{ 'text-primary font-weight-bold': !!(prSearchQuery && prSearchQuery.trim()) }"></i>
                            </b-input-group-prepend>
                            <b-form-input
                                v-model="prSearchQuery"
                                placeholder="Filter title or !ID..."
                                aria-label="Filter pull requests by title or ID"
                                data-test="input-pr-search"
                                style="height: 31px; font-size: 0.82rem;"
                            ></b-form-input>
                            <b-input-group-append v-if="prSearchQuery">
                                <b-button size="sm" variant="outline-secondary" aria-label="Clear search" title="Clear search" data-test="btn-clear-pr-search" @click="prSearchQuery = ''" style="height: 31px;">
                                    <i class="bi bi-x"></i>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </template>
                    <template #view-actions>
                        <span v-if="hasFreeDailyQuota && dailyMutationsRemaining > 0"
                            class="badge badge-info px-2 py-1 mr-2 font-weight-bold"
                            data-test="badge-free-mutation-quota"
                            v-b-tooltip.hover :title="`Free tier includes ${dailyMutationLimit} platform actions per UTC day. Upgrade to Pro Plan for unlimited two-way sync.`">
                            <i class="bi bi-lightning-charge-fill mr-1"></i>{{ dailyMutationsRemaining }}/{{ dailyMutationLimit }} Free Actions Left
                        </span>
                        <span v-else-if="isViewOnlyMode" class="badge badge-warning text-dark px-2 py-1 mr-2 font-weight-bold"
                            data-test="badge-view-only"
                            v-b-tooltip.hover title="Daily free action quota (5/5) used. Upgrade to Pro Plan for unlimited PR status updates, title editing, and active branch workflows, or wait until 00:00 UTC.">
                            <i class="bi bi-eye mr-1"></i>View-Only Mode (0/5 Left Today)
                        </span>
                    </template>
                    <template v-if="false">
                        <div class="filter-toolbar-group filter-toolbar-view d-flex align-items-center flex-wrap w-100">
                            <div class="filter-toolbar-label mr-2">
                                <i class="bi bi-eye mr-1"></i><strong>View:</strong>
                            </div>

                            <!-- Grouping chip (2 options: No Grouping, By Repo) -->
                            <b-dropdown size="sm" :variant="groupMode !== 'none' ? 'primary' : 'outline-secondary'"
                                no-caret class="grid-filter-chip"
                                data-test="filter-chip-grouping">
                                <template #button-content>
                                    <i class="bi bi-folder2-open mr-1"></i>
                                    <span class="font-weight-bold mr-1">Grouping:</span>
                                    <span>{{ groupModeLabel }}</span>
                                    <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                                </template>
                                <b-dropdown-item-button :active="groupMode === 'none'" @click="groupMode = 'none'" data-test="grouping-option-none">
                                    <i class="bi bi-list-ul mr-2"></i>No Grouping
                                </b-dropdown-item-button>
                                <b-dropdown-item-button :active="groupMode === 'repo'" @click="groupMode = 'repo'" data-test="grouping-option-repo">
                                    <i class="icon-azure-pr mr-2"></i>By Repo
                                </b-dropdown-item-button>
                            </b-dropdown>

                            <!-- View-Only Mode Badge -->
                            <span v-if="isViewOnlyMode" class="badge badge-warning text-dark px-2 py-1 ml-2 font-weight-bold"
                                data-test="badge-view-only"
                                v-b-tooltip.hover title="Free tier is in View-Only mode. Upgrading to Pro Plan unlocks PR status updates, title editing, and active branch workflows.">
                                <i class="bi bi-eye mr-1"></i>View-Only Mode
                            </span>
                        </div>
                    </template>
                    <template #header-collapser>
                        <b-button @click="toggleCollapseAll" variant="secondary" size="sm"
                            aria-label="Toggle Collapse ALL"
                            title="Toggle Collapse ALL"
                            data-test="btn-toggle-collapse-all"
                            v-b-popover.hover.top.noninteractive="actionHoverPop('Toggle Collapse ALL')">
                            <i class="bi bi-chevron-right row-expand-icon" :class="{ 'is-expanded': isAllCollapsesOpen }"></i>
                        </b-button>
                    </template>
                    <template #header-id>
                        <span>PR</span>
                    </template>
                    <template #header-status>
                        <div class="d-flex align-items-center justify-content-center">
                            <span>Status</span>
                            <i v-if="isStatusFilterActive" class="bi bi-funnel-fill column-filter-icon"
                                v-b-tooltip.hover.top="'Filter applied'"></i>
                        </div>
                    </template>
                    <template #header-reviewers>
                        <div class="d-flex align-items-center justify-content-center">
                            <span>Reviewers</span>
                            <i v-if="isReviewersFilterActive" class="bi bi-funnel-fill column-filter-icon"
                                v-b-tooltip.hover.top="'Showing optional reviewers'"></i>
                        </div>
                    </template>
                    <template #header-workitems>
                        <div class="d-flex align-items-center justify-content-center">
                            <span>Work Items</span>
                            <i v-if="isWorkItemsFilterActive" class="bi bi-funnel-fill column-filter-icon"
                                v-b-tooltip.hover.top="'Showing all work items'"></i>
                        </div>
                    </template>

                    <!--Groups-->
                    <div v-for="(group, pIndex) in visibleGroups" :key="group.groupId">
                        <b-card style="margin: 10px 0px;">
                            <!--Group header row-->
                            <b-row class="my-1 grid-row grid-project-row pr-project-row" v-if="!group.isFlat">
                                <b-col class="px-1 pr-col-collapser">
                                    <b-button variant="secondary" @click="toggleCollapseSingle(pIndex)" size="sm"
                                        aria-label="Toggle collapse group"
                                        title="Toggle Collapse"
                                        v-b-popover.hover.top.noninteractive="actionHoverPop('Toggle Collapse')"
                                        v-if="group.pullRequests.length > 0">
                                        <i class="bi bi-chevron-right row-expand-icon" :class="{ 'is-expanded': !collapsesStates[pIndex] }"></i>
                                    </b-button>
                                </b-col>
                                <b-col class="px-1 pr-project-name">
                                    <i class="bi bi-folder2-open mr-1"></i>
                                    <strong>{{ group.groupName }}</strong>
                                    <b-badge variant="light" class="ml-2">{{ group.pullRequests.length }}</b-badge>
                                </b-col>
                            </b-row>

                            <!--Pull requests-->
                            <b-collapse :visible="!collapsesStates[pIndex]">
                                <div v-for="pr in group.pullRequests" :key="pr.id" class="pr-item-wrap">
                                    <b-row align-h="start"
                                        :class="['my-1', 'grid-row', 'pr-item-row', { 'pr-row-clamped': !pr.expanded, 'pr-row-editing-branch': editingTargetBranchPrId === pr.id }]"
                                        data-test="grid-row-pr"
                                        :data-test-id="pr.id">
                                        <b-col class="px-1 pr-col-collapser"></b-col>
                                        <template v-for="col in visibleDataColumns">

                                        <!--PR id / navigate to Azure-->
                                        <b-col v-if="col.key === 'id'" :key="col.key" class="px-1 pr-col-id">

                                            <nav-link-button :href="getPrAdoUrl(pr)" :id="pr.id" prefix="!" is-pr
                                                title="Open PR in Azure DevOps" />
                                        </b-col>

                                        <!--Title-->
                                        <b-col v-else-if="col.key === 'title'" :key="col.key" class="px-1 pr-col-title">

                                            <grid-title-textarea v-model="pr.titleDraft"
                                                :disabled="pr.savingTitle || isRowLocked(pr)">
                                            </grid-title-textarea>
                                            <div v-if="hasVisiblePrRowBadges(pr)" class="pr-attention-bar" data-test="pr-attention-bar">
                                                <span v-if="isAwaitingMyReview(pr)"
                                                    class="pr-attention-pill pr-attention-pill-re-review pr-attention-pill-urgent"
                                                    data-test="badge-pr-awaiting-my-review"
                                                    v-b-tooltip.hover.top.html="awaitingMyReviewTooltipHtml(pr)">
                                                    <i class="bi bi-person-check-fill mr-1"></i>
                                                    <span>Awaiting Your Review</span>
                                                </span>
                                                <span v-if="isPrUpdatedSinceReview(pr)"
                                                    class="pr-attention-pill pr-attention-pill-re-review"
                                                    :class="{ 'pr-attention-pill-urgent': isCurrentUserWaitingReviewer(pr) }"
                                                    data-test="badge-pr-updated-since-review"
                                                    v-b-tooltip.hover.top.html="updatedSinceReviewTooltip(pr)">
                                                    <i class="bi bi-arrow-repeat mr-1"></i>
                                                    <span v-if="isCurrentUserWaitingReviewer(pr)">Updated Since Your Review — Re-review Needed</span>
                                                    <span v-else>Updated Since Review — Re-review Needed<template v-if="pr.waitingReviewerNames && pr.waitingReviewerNames.length"> ({{ pr.waitingReviewerNames.join(', ') }})</template></span>
                                                </span>
                                                <span v-if="isPrAutoComplete(pr)"
                                                    class="pr-attention-pill pr-attention-pill-auto-complete"
                                                    data-test="badge-pr-auto-complete"
                                                    v-b-tooltip.hover.top.html="autoCompleteTooltipHtml">
                                                    <i class="bi bi-lightning-charge-fill mr-1"></i>
                                                    <span>Auto-Complete</span>
                                                </span>
                                                <span v-if="isPrReadyToMerge(pr)"
                                                    class="pr-attention-pill pr-attention-pill-ready-merge"
                                                    data-test="badge-pr-ready-to-merge"
                                                    v-b-tooltip.hover.top.html="readyToMergeTooltipHtml">
                                                    <i class="bi bi-check2-all mr-1"></i>
                                                    <span>Ready to Merge</span>
                                                </span>
                                                <span v-if="isPrConflictSignalVisible(pr)"
                                                    class="pr-attention-pill pr-attention-pill-conflict"
                                                    data-test="badge-pr-conflict-signal"
                                                    v-b-tooltip.hover.top.html="pr.hasConflicts ? mergeConflictsTooltipHtml : blockerTooltip(pr)">
                                                    <i :class="[pr.hasConflicts ? 'bi bi-exclamation-triangle-fill' : 'bi bi-shield-fill-x', 'mr-1']"></i>
                                                    <span>{{ pr.hasConflicts ? 'Merge Conflicts' : 'Blocked' }}</span>
                                                </span>
                                                <span v-if="isPrReviewerOnPto(pr)"
                                                    class="pr-attention-pill pr-attention-pill-re-review"
                                                    data-test="badge-reviewer-on-pto"
                                                    v-b-tooltip.hover.top.html="reviewerOnPtoTooltip(pr)">
                                                    <i class="bi bi-airplane mr-1"></i>
                                                    <span>Reviewer Out of Office ({{ reviewersOnPto(pr).map(r => r.name).join(', ') }})</span>
                                                </span>
                                                <span v-if="isPrCiFailed(pr)"
                                                    class="pr-attention-pill pr-attention-pill-conflict"
                                                    data-test="badge-pr-ci-failed"
                                                    v-b-tooltip.hover.top.html="ciFailedTooltip(pr)">
                                                    <i class="bi bi-x-octagon-fill mr-1"></i>
                                                    <span>CI Failing</span>
                                                    <a v-if="pr.ciBuildUrl"
                                                        :href="pr.ciBuildUrl"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        class="pr-attention-pill-link ml-1"
                                                        data-test="link-pr-ci-build-log"
                                                        aria-label="Open failing build log"
                                                        title="Open failing build log"
                                                        @click.stop>
                                                        <i class="bi bi-box-arrow-up-right"></i>
                                                    </a>
                                                </span>
                                                <span v-if="isPrMergedWorkItemOpen(pr)"
                                                    class="pr-attention-pill pr-attention-pill-ready-merge"
                                                    data-test="badge-pr-merged-wi-open"
                                                    v-b-tooltip.hover.top.html="mergedWiOpenTooltipHtml">
                                                    <i class="bi bi-git mr-1"></i>
                                                    <span>PR Merged — Ready to Resolve/Close?</span>
                                                    <button v-for="wi in openMergedWorkItems(pr)"
                                                        :key="wi.id"
                                                        type="button"
                                                        class="pr-merged-wi-btn ml-1"
                                                        data-test="btn-resolve-merged-work-item"
                                                        :data-test-id="wi.id"
                                                        :data-state="resolvingWorkItemIds[wi.id] ? 'loading' : 'idle'"
                                                        :aria-busy="resolvingWorkItemIds[wi.id] ? 'true' : 'false'"
                                                        :disabled="!!resolvingWorkItemIds[wi.id] || !wi.suggestedResolveState"
                                                        :aria-label="`Move work item #${wi.id} to ${wi.suggestedResolveState}`"
                                                        :title="`Move work item #${wi.id} (${wi.state}) to ${wi.suggestedResolveState}`"
                                                        @click.stop="resolveMergedWorkItemAsync(pr, wi)">
                                                        #{{ wi.id }} &rarr; {{ wi.suggestedResolveState }}
                                                    </button>
                                                </span>
                                                <span v-if="isPrActiveThreadsVisible(pr)"
                                                    role="button"
                                                    tabindex="0"
                                                    class="pr-attention-pill pr-attention-pill-active-threads"
                                                    data-test="badge-pr-active-threads"
                                                    v-b-tooltip.hover.top.html="activeThreadsTooltipHtml(pr)"
                                                    @click.stop="openActiveComments(pr)"
                                                    @keydown.enter.prevent="openActiveComments(pr)">
                                                    <i class="bi bi-chat-left-text-fill mr-1"></i>
                                                    <span>{{ activeThreadCount(pr) === 1 ? '1 Active Thread' : `${activeThreadCount(pr)} Active Threads` }}</span>
                                                </span>
                                            </div>
                                        </b-col>

                                        <!--Status-->
                                        <b-col v-else-if="col.key === 'status'" :key="col.key" class="px-1 pr-col-status text-center">

                                            <status-badge :status="statusLabel(pr)" :variant="statusVariant(pr)" :custom-color="prStatusColor(statusLabel(pr))" data-test="badge-pr-status" />
                                        </b-col>

                                        <!--Reviewers / approvals-->
                                        <b-col v-else-if="col.key === 'reviewers'" :key="col.key" class="px-1 pr-col-reviewers">
                                            <label class="mobile-col-caption d-lg-none">Reviewers</label>
                                            <div v-for="(reviewer, rIndex) in visibleReviewers(pr)" :key="rIndex"
                                                class="pr-reviewer">
                                                <b-img class="pr-reviewer-image" :src="getReviewerAvatarSrc(reviewer)" rounded="circle"
                                                    v-b-popover.hover.top="reviewer.name"
                                                    @error="onReviewerImageError($event, reviewer)"></b-img>
                                                <span class="pr-reviewer-required text-muted ml-1"
                                                    v-b-popover.hover.top.noninteractive="actionHoverPop(reviewer.isRequired ? 'Required reviewer' : 'Optional reviewer')">
                                                    <i
                                                        :class="reviewer.isRequired ? 'bi bi-shield-fill-check' : 'bi bi-shield'"></i>
                                                </span>
                                                <b-badge :variant="voteVariant(reviewer.vote)"
                                                    class="ml-1 pr-reviewer-vote"
                                                    v-b-tooltip.hover.top.html="reviewerVoteTooltip(reviewer)">{{
                                                        shortVoteStatus(reviewer) }}</b-badge>
                                            </div>
                                        </b-col>

                                        <!--Flags-->
                                        <b-col v-else-if="col.key === 'flags'" :key="col.key" class="px-1 pr-col-flags text-center">
                                            <i v-if="false && pr.isBlocked"
                                                class="bi bi-shield-fill-x text-danger mr-1 pr-blocker-icon"
                                                v-b-popover.hover.top.noninteractive="actionHoverPop(blockerTooltip(pr))"></i>
                                            <i v-else-if="false && pr.qualityGateStatus === 'Passed'"
                                                class="bi bi-shield-fill-check text-success mr-1 pr-passed-icon"
                                                data-test="badge-quality-gate"
                                                v-b-popover.hover.top.noninteractive="actionHoverPop('Quality gates and checks passed')"></i>
                                            <i v-else-if="false && pr.qualityGateStatus === 'Pending'"
                                                class="bi bi-hourglass-split text-warning mr-1 pr-pending-icon"
                                                data-test="badge-quality-gate"
                                                v-b-popover.hover.top.noninteractive="actionHoverPop('Checks pending...')"></i>
                                            <i v-if="false && pr.hasConflicts"
                                                class="bi bi-exclamation-triangle-fill text-danger mr-1"
                                                v-b-popover.hover.top.noninteractive="actionHoverPop('Merge conflicts detected')"></i>
                                            <i v-if="isPrEffectivelyBlocked(pr)"
                                                class="bi bi-shield-fill-x text-danger mr-1 pr-blocker-icon"
                                                v-b-tooltip.hover.top.html="blockerTooltip(pr)"></i>
                                            <i v-else-if="pr.qualityGateStatus === 'Passed'"
                                                class="bi bi-shield-fill-check text-success mr-1 pr-passed-icon"
                                                data-test="badge-quality-gate"
                                                v-b-popover.hover.top.noninteractive="actionHoverPop('Quality gates and checks passed')"></i>
                                            <i v-else-if="pr.qualityGateStatus === 'Pending'"
                                                class="bi bi-hourglass-split text-warning mr-1 pr-pending-icon"
                                                data-test="badge-quality-gate"
                                                v-b-popover.hover.top.noninteractive="actionHoverPop('Checks pending...')"></i>
                                            <i v-if="pr.hasConflicts"
                                                class="bi bi-exclamation-triangle-fill text-danger mr-1"
                                                v-b-popover.hover.top.noninteractive="actionHoverPop('Merge conflicts detected')"></i>
                                            <span v-if="pr.commentGroups && pr.commentGroups.length" class="pr-comments">
                                                <b-button v-for="(g, gIndex) in pr.commentGroups" :key="gIndex"
                                                    variant="link" class="pr-comment-group p-0" :class="commentClass(g)"
                                                    aria-label="Open pull request comments"
                                                    title="Open pull request comments"
                                                    data-test="btn-open-pr-comments"
                                                    @click="openComments(pr, g)"
                                                    v-b-popover.hover.top.noninteractive="actionHoverPop(commentStatusLabel(g.status) + ' comments')">
                                                    <i class="bi bi-chat-left-text-fill"></i>
                                                    <span class="pr-comment-count">{{ g.count }}</span>
                                                </b-button>
                                            </span>
                                            <span
                                                v-if="!hasVisiblePrFlags(pr)"
                                                class="pr-empty">–</span>
                                        </b-col>

                                        <!--Branches-->
                                        <b-col v-else-if="col.key === 'branches'" :key="col.key" class="px-1 pr-col-branches">
                                            <label class="mobile-col-caption d-lg-none">Branches</label>
                                            <div v-if="showRepoInBranches" class="pr-branch pr-branch-repo">
                                                <span class="text-muted mr-1">repo</span>
                                                <b-badge variant="secondary"
                                                    v-b-popover.hover.top.noninteractive="actionHoverPop(pr.repositoryName)">{{
                                                        pr.repositoryName }}</b-badge>
                                            </div>
                                            <div class="pr-branch">
                                                <span class="text-muted mr-1">from</span>
                                                <b-badge :variant="branchVariant(pr.sourceBranch)">{{ pr.sourceBranch
                                                }}</b-badge>
                                            </div>
                                            <div class="pr-branch">
                                                <span class="text-muted mr-1">into</span>
                                                <div :ref="'targetBranchGroup-' + pr.id"
                                                    class="pr-target-branch-group"
                                                    :class="{ 'can-switch': canSwitchTargetBranch(pr), 'is-editing': editingTargetBranchPrId === pr.id }"
                                                    data-test="target-branch-group">
                                                    <b-badge :variant="branchVariant(pr.targetBranch)"
                                                        class="pr-target-branch-badge"
                                                        data-test="badge-pr-target-branch"
                                                        @click.stop="canSwitchTargetBranch(pr) && openTargetBranchPicker(pr, $event)">{{ pr.targetBranch }}</b-badge>
                                                    <span v-if="canSwitchTargetBranch(pr)" class="pr-target-branch-tray">
                                                        <span class="pr-target-branch-pipe"></span>
                                                        <button type="button"
                                                            class="pr-target-branch-switch-btn"
                                                            data-test="btn-switch-target-branch"
                                                            aria-label="Switch target branch"
                                                            title="Switch target branch"
                                                            v-b-popover.hover.top.noninteractive="actionHoverPop('Switch target branch')"
                                                            @click.stop.prevent="openTargetBranchPicker(pr, $event)">
                                                            <i class="bi bi-arrow-left-right"></i>
                                                        </button>
                                                    </span>
                                                </div>
                                                <div v-if="editingTargetBranchPrId === pr.id"
                                                    class="pr-target-branch-editor"
                                                    :class="{ 'open-upward': targetBranchDropUp }"
                                                    :style="targetBranchOverlayStyle"
                                                    data-test="editor-target-branch"
                                                    @click.stop>
                                                    <div class="pr-target-branch-input-wrap">
                                                        <input
                                                            :ref="'targetBranchInput-' + pr.id"
                                                            v-model="targetBranchSearch"
                                                            type="text"
                                                            class="form-control form-control-sm pr-target-branch-input"
                                                            placeholder="Search or select branch..."
                                                            data-test="input-target-branch"
                                                            @input="onTargetBranchSearchInput(pr)"
                                                            @keydown.down.prevent="moveTargetBranchHighlight(pr, 1)"
                                                            @keydown.up.prevent="moveTargetBranchHighlight(pr, -1)"
                                                            @keydown.enter.prevent="onTargetBranchEnter(pr)"
                                                            @keydown.esc.prevent="closeTargetBranchPicker()"
                                                        />
                                                        <button type="button"
                                                            class="pr-target-branch-cancel-btn"
                                                            data-test="btn-cancel-target-branch"
                                                            aria-label="Cancel target branch switch"
                                                            title="Cancel (Esc)"
                                                            @click.stop.prevent="closeTargetBranchPicker()">
                                                            <i class="bi bi-x-lg"></i>
                                                        </button>
                                                    </div>
                                                    <div class="pr-target-branch-dropdown" data-test="dropdown-target-branch">
                                                        <div v-if="loadingTargetBranches" class="pr-target-branch-loading">
                                                            <b-spinner small class="mr-1"></b-spinner> Loading branches...
                                                        </div>
                                                        <div v-for="section in getTargetBranchSections(pr)" :key="section.key" class="pr-target-branch-section">
                                                            <div class="pr-target-branch-section-hdr">
                                                                <i :class="[section.icon, 'mr-1']"></i>{{ section.label }}
                                                            </div>
                                                            <button v-for="opt in section.items"
                                                                :key="section.key + '-' + opt.name"
                                                                type="button"
                                                                class="pr-target-branch-option"
                                                                :class="{
                                                                    'is-current': opt.isCurrent,
                                                                    'is-highlighted': opt.flatIndex === targetBranchHighlightIdx
                                                                }"
                                                                data-test="option-target-branch"
                                                                :data-branch-name="opt.name"
                                                                @mouseenter="targetBranchHighlightIdx = opt.flatIndex"
                                                                @mousedown.prevent="selectTargetBranch(pr, opt.name)">
                                                                <span class="pr-target-branch-opt-main">
                                                                    <b-badge :variant="branchVariant(opt.name)" class="pr-target-branch-opt-badge">{{ opt.name }}</b-badge>
                                                                    <span v-if="opt.badge" class="pr-target-branch-opt-tag" :class="'is-' + (opt.badgeType || 'special')">{{ opt.badge }}</span>
                                                                </span>
                                                                <i v-if="opt.isCurrent" class="bi bi-check2 text-success ml-2" title="Current target branch"></i>
                                                            </button>
                                                        </div>
                                                        <div v-if="!getTargetBranchFlatOptions(pr).length" class="pr-target-branch-empty">
                                                            No matching branches
                                                        </div>
                                                    </div>
                                                </div>
                                                <b-badge v-if="false">{{ pr.targetBranch
                                                }}</b-badge>
                                            </div>
                                        </b-col>

                                        <!--Work items-->
                                        <b-col v-else-if="col.key === 'workitems'" :key="col.key" class="px-1 pr-col-workitems">
                                            <label class="mobile-col-caption d-lg-none">Work Items</label>
                                            <div v-for="wi in pr.workItems" :key="wi.id" class="pr-workitem-line">
                                                <nav-link-button :href="getWorkItemAdoUrl(wi, pr)" :id="wi.id" prefix="#"
                                                    title="Open work item in Azure DevOps"
                                                    custom-class="pr-workitem-btn" />
                                                <span class="pr-workitem-title">{{ wi.title }}</span>
                                            </div>
                                            <span v-if="!pr.workItems.length" class="pr-empty">–</span>
                                        </b-col>

                                        <!--Action buttons-->
                                        <b-col v-else-if="col.key === 'buttons'" :key="col.key" class="px-1 pr-col-buttons">
                                            <grid-button-section :buttons="prActionButtons(pr)"
                                                @action="onPrAction(pr, $event)"></grid-button-section>
                                        </b-col>
                                        </template>
                                        <b-col v-if="false">
                                        </b-col>
                                    </b-row>
                                    <!--Show more/less expander bar-->
                                    <div v-if="pr.expanded || isRowOverflowing(pr)" class="pr-expander"
                                        @click="pr.expanded = !pr.expanded"
                                        v-b-popover.hover.top.noninteractive="actionHoverPop(pr.expanded ? 'Show less' : 'Show more')">
                                        <i :class="pr.expanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
                                    </div>
                                </div>
                            </b-collapse>
                        </b-card>
                    </div>
                </grid-shell>
                <div v-else-if="!loadingPullRequests" class="text-center py-5 text-muted">
                    <i class="icon-azure-pr fa-2x mb-2 d-block"></i>
                    <span>Select a sprint to view pull requests.</span>
                </div>
            </b-overlay>
            </template>
        </div>

        <modal-task-columns
            ref="modalPrColumns"
            modal-id="modal-pr-columns"
            modal-title="Customize Pull Request Columns & Attention Setup"
            subtitle="Drag and drop column badges below to customize their display sequence in the Pull Requests grid. Use the eye icon to show or hide columns, or the palette icon on Status to customize status badge colors. The Action column always remains fixed at the end."
            :allow-custom-fields="false"
            storage-key="vt_pr_columns_config"
            fixed-action-ref="buttons"
            :default-columns="defaultPrColumns"
            :current-columns="activeGridColumns"
            :feature-config="prFeatureConfig"
            @columns-saved="onColumnsSaved"
            @feature-settings-saved="onPrFeatureSettingsSaved"
        />

        <b-modal v-model="showCommentsModal" :title="expandedTitle" ok-only ok-title="Close" scrollable size="lg"
            modal-class="pr-comments-modal" body-class="py-2 px-3 pr-comments-modal-body" data-test="modal-pr-comments">
            <b-overlay :show="loadingComments" rounded="lg" opacity="0.6" class="pr-comments-overlay">
                <template #overlay>
                    <load-spinner text="Loading discussion comments..." size="sm" />
                </template>
                <div v-for="(c, i) in pagedComments" :key="c.threadId || i" class="pr-comment-line pr-comment-thread"
                    data-test="pr-comment-thread" :data-thread-id="c.threadId || (i + 1)">
                    <div class="pr-thread-header d-flex align-items-center justify-content-between flex-wrap mb-1">
                        <div class="d-flex align-items-center flex-wrap">
                            <b-badge :variant="commentVariant(c.status)" class="mr-2 pr-comment-status"
                                data-test="badge-pr-thread-status">{{ commentStatusLabel(c.status) }}</b-badge>
                            <span v-if="c.authorName" class="pr-comment-author font-weight-bold mr-2">{{ c.authorName }}</span>
                        </div>
                        <div class="pr-thread-actions d-flex align-items-center flex-wrap">
                            <b-button v-for="action in getThreadTransitions(c)" :key="action.actionKey"
                                size="sm" :variant="action.variant || 'outline-secondary'"
                                class="pr-thread-action-btn mr-1"
                                :disabled="c.updatingStatus || c.submittingReply"
                                :data-state="c.updatingStatus ? 'loading' : 'idle'"
                                :data-test="'btn-pr-thread-' + action.actionKey"
                                :aria-label="action.label + ' comment thread'"
                                :title="action.label + ' comment thread'"
                                @click="changeThreadStatus(c, action)">
                                {{ action.label }}
                            </b-button>
                        </div>
                    </div>
                    <div class="pr-comment-text" v-html="renderComment(c.text)"></div>

                    <div v-if="c.replies && c.replies.length" class="pr-thread-replies mt-2" data-test="pr-thread-replies">
                        <div v-for="(reply, rIdx) in c.replies" :key="reply.commentId || rIdx"
                            class="pr-thread-reply-item" data-test="pr-thread-reply-item" :data-reply-id="reply.commentId || (rIdx + 1)">
                            <div class="pr-thread-reply-meta d-flex align-items-center justify-content-between">
                                <div class="d-flex align-items-center">
                                    <i class="bi bi-arrow-return-right mr-1 text-muted"></i>
                                    <strong class="pr-reply-author mr-2">{{ reply.authorName || 'You' }}</strong>
                                </div>
                                <div v-if="!isViewOnlyMode && !reply.editing" class="pr-reply-actions d-flex align-items-center">
                                    <b-button size="sm" variant="link" class="p-0 px-1 text-secondary pr-reply-action-btn"
                                        :disabled="reply.saving || reply.deleting"
                                        data-test="btn-edit-pr-reply"
                                        aria-label="Edit reply"
                                        title="Edit reply"
                                        @click="startEditThreadReply(reply)">
                                        <i class="bi bi-pencil mr-1"></i>Edit
                                    </b-button>
                                    <span class="text-muted mx-1">|</span>
                                    <b-button size="sm" variant="link" class="p-0 px-1 text-danger pr-reply-action-btn"
                                        :disabled="reply.saving || reply.deleting"
                                        :data-state="reply.deleting ? 'loading' : 'idle'"
                                        data-test="btn-delete-pr-reply"
                                        aria-label="Delete reply"
                                        title="Delete reply"
                                        @click="deleteThreadReply(c, reply)">
                                        <i class="bi bi-trash mr-1"></i>Delete
                                    </b-button>
                                </div>
                            </div>
                            <div v-if="!reply.editing" class="pr-comment-text pr-reply-text" v-html="renderComment(reply.text)"></div>
                            <div v-else class="pr-reply-edit-box mt-1" data-test="pr-reply-edit-box">
                                <b-form-textarea v-model="reply.editDraft" rows="3" max-rows="5" size="sm"
                                    data-test="input-edit-pr-reply"
                                    :disabled="reply.saving">
                                </b-form-textarea>
                                <div class="d-flex justify-content-end align-items-center mt-1">
                                    <b-button size="sm" variant="outline-secondary" class="mr-1"
                                        data-test="btn-cancel-edit-pr-reply"
                                        :disabled="reply.saving"
                                        @click="cancelEditThreadReply(reply)">
                                        Cancel
                                    </b-button>
                                    <b-button size="sm" variant="primary"
                                        data-test="btn-save-edit-pr-reply"
                                        :data-state="reply.saving ? 'loading' : 'idle'"
                                        :disabled="reply.saving || !reply.editDraft || !reply.editDraft.trim()"
                                        @click="saveEditThreadReply(c, reply)">
                                        <i class="bi bi-check2 mr-1"></i>Save
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="!isViewOnlyMode" class="pr-thread-reply-box mt-2" data-test="pr-thread-reply-box">
                        <b-form-textarea v-model="c.replyDraft"
                            :rows="c.replyFocused || (c.replyDraft && c.replyDraft.trim().length > 0) ? 3 : 1"
                            max-rows="4" size="sm"
                            placeholder="Write a reply to this review thread..."
                            data-test="input-pr-thread-reply"
                            :disabled="c.submittingReply"
                            @focus="onReplyFocus(c)"
                            @blur="onReplyBlur(c)">
                        </b-form-textarea>
                        <div v-if="c.replyFocused || (c.replyDraft && c.replyDraft.trim().length > 0) || c.submittingReply"
                             class="d-flex justify-content-end align-items-center mt-1">
                            <b-button size="sm" variant="primary"
                                data-test="btn-submit-pr-thread-reply"
                                :data-state="c.submittingReply ? 'loading' : 'idle'"
                                :disabled="c.submittingReply || !c.replyDraft || !c.replyDraft.trim()"
                                @mousedown.prevent
                                @click="submitThreadReply(c)">
                                <i class="bi bi-send-fill mr-1"></i>Send Reply
                            </b-button>
                        </div>
                    </div>
                </div>
                <b-pagination v-if="expandedComments.length > commentsPerPage" v-model="commentsPage"
                    :total-rows="expandedComments.length" :per-page="commentsPerPage" size="sm" align="center"
                    class="mt-2 mb-0">
                </b-pagination>
                <div v-if="!loadingComments && !expandedComments.length" class="text-muted">No comments</div>
            </b-overlay>
        </b-modal>
    </div>
</template>

<script>
import { debounce } from 'lodash';
import DOMPurify from 'dompurify';
import accountMixin from '../mixinAccount';
import ptoMixin from '../mixinPtoSignals';
import toastMixin from '../mixinToast';
import PickerSprints from './picker-sprints.vue';
import GridButtonSection from './grid-button-section.vue';
import GridShell from './grid-shell.vue';
import GridTitleTextarea from './grid-title-textarea.vue';
import NavLinkButton from './nav-link-button.vue';
import StatusBadge from './status-badge.vue';
import CardMissingSource from './card-missing-source.vue';
import FilterAuthorDropdown from './filter-author-dropdown.vue';
import GridHeaderBanner from './grid-header-banner.vue';
import ModalTaskColumns from './modal-task-columns.vue';

const PR_REPO_BRANCHES_CACHE_KEY = 'vt_pr_repo_branches_cache_v3';
const PR_REPO_BRANCHES_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

const DEFAULT_PR_STATUS_COLORS = {
    Active: '#0284c7',
    Draft: '#64748b',
    Completed: '#10b981',
    Abandoned: '#ef4444'
};

const DEFAULT_PR_COLUMNS = [
    { key: "id", referenceName: "id", label: "PR", width: "5rem", isSystem: true, order: 1, isVisible: true, hidden: false },
    { key: "title", referenceName: "title", label: "Title", width: "minmax(12rem, 2fr)", isSystem: true, order: 2, isVisible: true, hidden: false },
    {
        key: "status",
        referenceName: "status",
        label: "Status",
        width: "4.5rem",
        isSystem: true,
        order: 3,
        isVisible: true,
        hidden: false,
        allowedValues: ["Active", "Draft", "Completed", "Abandoned"],
        valueColors: { ...DEFAULT_PR_STATUS_COLORS }
    },
    { key: "reviewers", referenceName: "reviewers", label: "Reviewers", width: "7.5rem", isSystem: true, order: 4, isVisible: true, hidden: false },
    { key: "flags", referenceName: "flags", label: "Flags", width: "3.5rem", isSystem: true, order: 5, isVisible: true, hidden: false },
    { key: "branches", referenceName: "branches", label: "Branches", width: "minmax(9rem, 1fr)", isSystem: true, order: 6, isVisible: true, hidden: false },
    { key: "workitems", referenceName: "workitems", label: "Work Items", width: "minmax(10.5rem, 1.2fr)", isSystem: true, order: 7, isVisible: true, hidden: false },
    { key: "buttons", referenceName: "buttons", label: "Action", width: "6.75rem", isSystem: true, order: 8, isVisible: true, hidden: false }
];
import eventBus from '../event-bus';
import axios from 'axios';

export default {
    name: 'page-pull-requests',
    mixins: [accountMixin, toastMixin, ptoMixin],
    components: {
        'picker-sprints': PickerSprints,
        'grid-button-section': GridButtonSection,
        'grid-shell': GridShell,
        'grid-title-textarea': GridTitleTextarea,
        'nav-link-button': NavLinkButton,
        'status-badge': StatusBadge,
        'card-missing-source': CardMissingSource,
        'filter-author-dropdown': FilterAuthorDropdown,
        'grid-header-banner': GridHeaderBanner,
        'modal-task-columns': ModalTaskColumns
    },
    data() {
        return {
            selectedAuthors: ['me'],
            selectedReviewers: ['all'],
            statusFilter: [...this.$store.state.lastPrStatusFilter],
            statusFilterOpen: false,
            statusOptions: ["Active", "Draft", "Completed", "Abandoned"],
            showOptionalReviewers: this.$store.state.lastPrShowOptionalReviewers,
            showUnassigned: this.$store.state.lastPrShowUnassigned,
            appliedAuthors: ['me'],
            appliedReviewers: ['all'],
            appliedStatusFilter: [...this.$store.state.lastPrStatusFilter],
            appliedShowOptionalReviewers: this.$store.state.lastPrShowOptionalReviewers,
            appliedShowUnassigned: this.$store.state.lastPrShowUnassigned,
            debouncedLoadPullRequests: null,
            showCommentsModal: false,
            expandedTitle: "",
            expandedPr: null,
            expandedGroupStatus: "",
            allPrComments: [],
            expandedComments: [],
            commentsPage: 1,
            commentsPerPage: 20,
            loadingComments: false,
            loadingPullRequests: false,
            prAbortController: null,
            selectedSprint: "",
            groupMode: (this.$store.state.lastPrGroupMode === 'none' || this.$store.state.lastPrGroupMode === 'repo') ? this.$store.state.lastPrGroupMode : 'repo',
            groupOptions: [
                { value: "none", text: "No Grouping" },
                { value: "repo", text: "By Repo" }
            ],
            gridColumns: [
                { key: "collapser", label: "", width: "2.5rem" },
                { key: "id", label: "PR", width: "5rem" },
                { key: "title", label: "Title", width: "minmax(12rem, 2fr)" },
                { key: "status", label: "Status", width: "4.5rem" },
                { key: "reviewers", label: "Reviewers", width: "7.5rem" },
                { key: "flags", label: "Flags", width: "3.5rem" },
                { key: "branches", label: "Branches", width: "minmax(9rem, 1fr)" },
                { key: "workitems", label: "Work Items", width: "minmax(10.5rem, 1.2fr)" },
                { key: "buttons", label: "Action", width: "6.75rem" }
            ],
            collapsesStates: [],
            pullRequests: [],
            hasSprints: false,
            prSearchQuery: '',
            onlyAttentionPrs: false,
            resolvingWorkItemIds: {},
            defaultPrColumns: DEFAULT_PR_COLUMNS,
            activeGridColumns: this.loadStoredPrColumns(),
            prFeatureSettings: this.loadStoredPrFeatureSettings(),
            editingTargetBranchPrId: null,
            targetBranchSearch: '',
            targetBranchHighlightIdx: 0,
            targetBranchOverlayStyle: {},
            targetBranchDropUp: false,
            loadingTargetBranches: false,
            repoBranchesCache: this.loadStoredRepoBranchesCache(),
            repoRealBranches: {}
        };
    },
    watch: {
        statusFilter(newVal) {
            this.$store.commit("setLastPrStatusFilter", newVal);
        },
        groupMode(newVal) {
            this.$store.commit("setLastPrGroupMode", newVal);
        },
        showOptionalReviewers(newVal) {
            this.$store.commit("setLastPrShowOptionalReviewers", newVal);
        },
        showUnassigned(newVal) {
            this.$store.commit("setLastPrShowUnassigned", newVal);
        },
        globalProjectId: {
            async handler() {
                if (!this.hasBacklogConnection) {
                    this.selectedSprint = null;
                    this.hasSprints = false;
                }
                await this.loadPullRequestsAsync();
            }
        },
        visibleGroups(groups) {
            if (this.collapsesStates.length !== groups.length) {
                this.collapsesStates = groups.map(() => false);
            }
        }
    },
    async created() {
        this.debouncedLoadPullRequests = debounce(this.loadPullRequestsAsync, 300);
        if (!this.isAuthenticated)
            return;
        if (!this.hasBacklogConnection && this.hasRepoSource) {
            this.loadPullRequestsAsync();
        }
        this.onServiceReconnected = () => {
            if (this.hasRepoSource && (!this.pullRequests || this.pullRequests.length === 0)) {
                this.loadPullRequestsAsync();
            }
        };
        eventBus.$on('service-reconnected', this.onServiceReconnected);
    },
    beforeUnmount() {
        if (this.prAbortController) {
            this.prAbortController.abort();
            this.prAbortController = null;
        }
        this.unbindBranchPickerOutsideClick();
        if (this.onServiceReconnected) {
            eventBus.$off('service-reconnected', this.onServiceReconnected);
        }
    },
    computed: {
        prFeatureConfig() {
            const cfg = this.prFeatureSettings || {};
            return {
                title: 'Pull Request Attention & Quality Signals',
                subtitle: 'Configure which attention signal badges are displayed under the PR title and included in the Needs Attention filter.',
                icon: 'icon-azure-pr',
                masterLabel: 'Enable PR Signals',
                masterTestId: 'toggle-pr-features-master',
                storageKey: 'vt_pr_feature_config',
                enabled: cfg.enabled !== false,
                items: [
                    {
                        key: 'showUpdatedSinceReview',
                        label: 'Updated Since Review ("Ball in Reviewer\'s Court")',
                        badgeText: 'Re-review Needed',
                        badgeVariant: 'amber',
                        badgeIcon: 'bi bi-arrow-repeat',
                        description: 'Highlight active PRs where the author pushed new commits after a "Waiting for author" (-5) or "Rejected" (-10) review.',
                        testId: 'toggle-pr-updated-since-review',
                        enabled: cfg.showUpdatedSinceReview !== false
                    },
                    {
                        key: 'showReadyToMerge',
                        label: 'Approved & Green ("Ready to Merge")',
                        badgeText: 'Ready to Merge',
                        badgeVariant: 'emerald',
                        badgeIcon: 'bi bi-check2-all',
                        description: 'Display a high-visibility badge when all required reviewers approved, 0 active comment threads remain, and no merge conflicts exist.',
                        testId: 'toggle-pr-ready-to-merge',
                        enabled: cfg.showReadyToMerge !== false
                    },
                    {
                        key: 'highlightConflicts',
                        label: 'Merge Conflict & Blocked Warnings',
                        badgeText: 'Conflicts / Blocked',
                        badgeVariant: 'rose',
                        badgeIcon: 'bi bi-shield-fill-x',
                        description: 'Display a signal badge under the PR title when merge conflicts or blocking policies exist.',
                        testId: 'toggle-pr-highlight-conflicts',
                        enabled: cfg.highlightConflicts !== false
                    },
                    {
                        key: 'showQualityGates',
                        label: 'Quality Gate & Check Indicators',
                        badgeText: 'Checks Passed',
                        badgeVariant: 'emerald',
                        badgeIcon: 'bi bi-shield-fill-check',
                        description: 'Display CI/CD quality gate pass and pending check icons in the Flags column.',
                        testId: 'toggle-pr-quality-gates',
                        enabled: cfg.showQualityGates !== false
                    },
                    {
                        key: 'showCommentGroups',
                        label: 'Active Discussion Threads Signal',
                        badgeText: 'Active Threads',
                        badgeVariant: 'amber',
                        badgeIcon: 'bi bi-chat-left-text-fill',
                        description: 'Display an interactive Active Threads signal badge under the PR title when unresolved discussion threads exist.',
                        testId: 'toggle-pr-comment-groups',
                        enabled: cfg.showCommentGroups !== false
                    },
                    {
                        key: 'showReviewerVotes',
                        label: 'Reviewer Vote Status Badges',
                        badgeText: 'Approved / Waiting',
                        badgeVariant: 'sky',
                        badgeIcon: 'bi bi-person-check-fill',
                        description: 'Display approval and vote status badges next to each reviewer avatar.',
                        testId: 'toggle-pr-reviewer-votes',
                        enabled: cfg.showReviewerVotes !== false
                    }
                , {
                        key: 'showCiFailed',
                        label: 'CI Failing (Build Validation)',
                        badgeText: 'CI Failing',
                        badgeVariant: 'rose',
                        badgeIcon: 'bi bi-x-octagon-fill',
                        description: 'Display a CI Failing badge with a deep link to the failing Azure Pipelines build log when the latest build validation or a required status check failed.',
                        testId: 'toggle-pr-ci-failed',
                        enabled: cfg.showCiFailed !== false
                    },
                    {
                        key: 'showMergedWorkItemOpen',
                        label: 'PR Merged — Work Item Still Open',
                        badgeText: 'Ready to Resolve/Close?',
                        badgeVariant: 'emerald',
                        badgeIcon: 'bi bi-git',
                        description: 'Flag merged pull requests whose linked work items are still New or Active, with a 1-click state transition.',
                        testId: 'toggle-pr-merged-work-item-open',
                        enabled: cfg.showMergedWorkItemOpen !== false
                    },
                    {
                        key: 'showReviewerOnPto',
                        label: 'Reviewer Out of Office (PTO)',
                        badgeText: 'Reviewer Out of Office',
                        badgeVariant: 'amber',
                        badgeIcon: 'bi bi-airplane',
                        description: 'Flag active PRs still waiting on a reviewer who has planned absence in the next few days, so the review can be reassigned. Requires the Team plan.',
                        testId: 'toggle-pr-reviewer-on-pto',
                        enabled: cfg.showReviewerOnPto !== false
                    }
                ].filter(item => item.key !== 'showQualityGates' && item.key !== 'showReviewerVotes')
            };
        },
        visibleDataColumns() {
            const cols = (this.activeGridColumns && this.activeGridColumns.length)
                ? this.activeGridColumns
                : this.defaultPrColumns;
            return cols.filter(c => !c.hidden && c.isVisible !== false);
        },
        visibleGridColumns() {
            return [
                { key: "collapser", referenceName: "collapser", label: "", width: "2.5rem", resizable: false },
                ...this.visibleDataColumns
            ];
        },
        attentionPrCount() {
            const list = this.pullRequests || [];
            return list.filter(pr => this.hasPrAttentionSignal(pr)).length;
        },
        prStats() {
            const list = this.pullRequests || [];

            const activeCount = list.filter(pr => pr.status === 'Active' && !pr.isDraft).length;
            const draftCount = list.filter(pr => pr.isDraft || pr.status === 'Draft').length;
            const completedCount = list.filter(pr => pr.status === 'Completed').length;

            const stats = [];

            stats.push({
                key: 'active',
                label: 'Active PRs',
                count: activeCount,
                icon: 'icon-azure-pr',
                variant: 'sky',
                testId: 'stat-prs-active',
                showZero: true,
                tooltip: 'Active pull requests currently open'
            });

            if (draftCount > 0) {
                stats.push({
                    key: 'draft',
                    label: 'Drafts',
                    count: draftCount,
                    icon: 'bi bi-file-earmark-code',
                    variant: 'slate',
                    testId: 'stat-prs-draft',
                    tooltip: 'Pull requests currently in draft status'
                });
            }

            if (completedCount > 0) {
                stats.push({
                    key: 'merged',
                    label: 'Merged',
                    count: completedCount,
                    icon: 'bi bi-check2-circle',
                    variant: 'emerald',
                    testId: 'stat-prs-completed',
                    tooltip: 'Completed / merged pull requests in this scope'
                });
            }

            return stats;
        },
        visibleGroups() {
            const q = (this.prSearchQuery || '').trim().toLowerCase().replace(/^!/, '');
            const filtered = (this.pullRequests || []).filter(pr => {
                if (!this.matchesAppliedReviewerFilter(pr)) {
                    return false;
                }
                if (this.onlyAttentionPrs && (!this.prFeatureSettings || this.prFeatureSettings.enabled !== false) && !this.hasPrAttentionSignal(pr)) {
                    return false;
                }
                if (!q) return true;
                const titleMatch = (pr.title || '').toLowerCase().includes(q);
                const idMatch = String(pr.id || '').toLowerCase().includes(q);
                return titleMatch || idMatch;
            });

            // No grouping: everything in one flat group.
            if (this.groupMode === "none") {
                const flat = {
                    groupId: "__all__",
                    groupName: "",
                    isFlat: true,
                    pullRequests: filtered
                };
                return flat.pullRequests.length ? [flat] : [];
            }

            const groups = {};
            filtered.forEach(pr => {
                const id = pr.repositoryName || 'unknown-repo';
                const name = pr.repositoryName || 'Unknown Repository';
                if (!groups[id]) {
                    groups[id] = {
                        groupId: id,
                        groupName: name,
                        pullRequests: []
                    };
                }
                groups[id].pullRequests.push(pr);
            });

            return Object.values(groups)
                .sort((a, b) => a.groupName.localeCompare(b.groupName));
        },
        groupModeLabel() {
            const opt = this.groupOptions.find(o => o.value === this.groupMode);
            return opt ? opt.text : "No Grouping";
        },
        isAllCollapsesOpen() {
            return this.collapsesStates.length > 0 && this.collapsesStates.every(s => !s);
        },
        showRepoInBranches() {
            return this.groupMode === "none";
        },
        isStatusFilterInvalid() {
            return !this.statusFilter || this.statusFilter.length === 0;
        },
        statusFilterText() {
            const count = this.statusFilter.length;
            if (count === 0) return "Invalid";
            if (count === this.statusOptions.length) return "All";
            return this.statusOptions
                .filter(o => this.statusFilter.includes(o))
                .join(", ");
        },
        statusFilterTooltip() {
            const selected = this.statusOptions.filter(o => this.statusFilter.includes(o));
            if (!selected.length) return "Filter by status";
            return `Filter by status: ${selected.join(", ")}`;
        },
        pagedComments() {
            const start = (this.commentsPage - 1) * this.commentsPerPage;
            return this.expandedComments.slice(start, start + this.commentsPerPage);
        },
        isAnyRowModifying() {
            return this.pullRequests.some(pr => this.isTitleDirty(pr));
        },
        isStatusFilterActive() {
            return this.statusFilter.length !== 1 || !this.statusFilter.includes("Active");
        },
        isReviewersFilterActive() {

            const rev = this.normalizeFilterList(this.selectedReviewers, 'all')[0];
            return !!rev && rev !== 'all' && rev !== 'anyone';
        },
        isWorkItemsFilterActive() {
            return this.showUnassigned;
        },
        isAuthorFilterActive() {
            const auth = this.normalizeFilterList(this.selectedAuthors, 'me')[0];
            return !auth || auth !== 'me';
        },
        hasUnappliedFilters() {
            if (!this.areArraysEqual(this.selectedAuthors, this.appliedAuthors)) return true;
            if (!this.areArraysEqual(this.statusFilter, this.appliedStatusFilter)) return true;
            if (!this.areArraysEqual(this.selectedReviewers, this.appliedReviewers)) return true;
            if (this.showUnassigned !== this.appliedShowUnassigned) return true;
            return false;
        },
        isAnyPrFilterActive() {
            return this.isAuthorFilterActive || this.isStatusFilterActive || this.isWorkItemsFilterActive || this.isReviewersFilterActive || this.onlyAttentionPrs || !!(this.prSearchQuery && this.prSearchQuery.trim());
        },
        activeProject() {
            return this.globalProject;
        },
        projectResolved() {
            return !!this.globalProject;
        },
        hasRepoSource() {
            const proj = this.globalProject;
            return !!proj && !!proj.repoLinkId;
        },
        isMissingRepoVisible() {
            if (!this.isAuthenticated) return false;
            return !this.hasRepoSource;
        },
        isServiceUnavailable() {
            return !!this.$store.state.isServiceUnavailable;
        },
        isGridVisible() {
            if (this.isServiceUnavailable) return false;
            if (!this.projectResolved || this.loadingPullRequests || !this.hasRepoSource) return false;
            if (this.hasBacklogConnection && this.hasVisibleSprints && !this.selectedSprint) return false;
            return true;
        },
        hasBacklogConnection() {
            const proj = this.globalProject;
            return !!proj && !!proj.backlogLinkId && !!proj.backlogProjectId;
        },
        hasVisibleSprints() {
            return this.hasRepoSource && this.hasBacklogConnection && this.hasSprints;
        },
        prEmptyTitle() {
            if (this.isAnyPrFilterActive) {
                return 'No pull requests match your filters';
            }
            return 'No pull requests found';
        },
        prEmptyMessage() {
            if (this.isAnyPrFilterActive) {
                return 'No pull requests match the current combination of authors, statuses, reviewer scopes, or sprint dates.';
            }
            return 'There are no active pull requests in this repository.';
        },
        prFilterSuggestions() {
            const suggestions = [];
            if (this.isAuthorFilterActive && this.selectedAuthors && this.selectedAuthors[0] === 'me') {
                suggestions.push({
                    text: "Switch author filter from 'Me' to your team or 'All Authors' to view PRs opened by colleagues.",
                    action: 'author-team',
                    actionLabel: 'View Team'
                });
            } else if (this.isAuthorFilterActive) {
                suggestions.push({
                    text: "Switch author filter to 'Me' or 'All Authors'.",
                    action: 'author-all',
                    actionLabel: 'All Authors'
                });
            } else {
                suggestions.push({
                    text: "Switch author filter from 'Me' to your team or 'All Authors' to view PRs opened by colleagues.",
                    action: 'author-team',
                    actionLabel: 'View Team'
                });
            }
            if (this.statusFilter.length < this.statusOptions.length) {
                suggestions.push({
                    text: "Enable Completed or Abandoned statuses to view historical pull requests.",
                    action: 'all-statuses',
                    actionLabel: 'All Statuses'
                });
            }
            if (this.hasBacklogConnection && !this.showUnassigned) {
                suggestions.push({
                    text: "Toggle Work Items filter to 'All Work Items' to include PRs without linked backlog items.",
                    action: 'toggle-workitems',
                    actionLabel: 'All Work Items'
                });
            }
            if (this.isReviewersFilterActive) {
                suggestions.push({
                    text: "Switch Reviewers filter to 'Anyone' to include pull requests regardless of assigned reviewer (including PRs with no reviewer).",
                    action: 'reviewers-all',
                    actionLabel: 'Anyone'
                });
            }
            if (this.showOptionalReviewers && !this.selectedReviewers) {
                suggestions.push({
                    text: "Switch Reviewers filter to 'Required Only'.",
                    action: 'reviewers-required',
                    actionLabel: 'Required Only'
                });
            }
            return suggestions;
        },
        prEmptyActions() {
            const actions = [];
            if (this.selectedAuthors && this.selectedAuthors[0] === 'me') {
                actions.push({
                    text: 'View Team PRs',
                    icon: 'bi bi-people',
                    action: 'author-team',
                    variant: 'outline-secondary'
                });
            }
            if (this.statusFilter.length < this.statusOptions.length) {
                actions.push({
                    text: 'All Statuses',
                    icon: 'bi bi-collection',
                    action: 'all-statuses',
                    variant: 'outline-secondary'
                });
            }
            return actions;
        },
        attentionFilterTooltipHtml() {
            return '<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-lightning-charge-fill text-warning mr-1"></i>Needs Attention Filter</div><div class="vt-rt-body">Shows pull requests with actionable signals:<ul class="vt-rt-list"><li>Awaiting your review vote</li><li>Unresolved discussion comments</li><li>Merge conflicts or active rejections</li><li>New commits pushed since your review</li><li>Failing CI build checks or Ready to Merge</li></ul></div></div>';
        },
        autoCompleteTooltipHtml() {
            return '<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-lightning-charge-fill text-info mr-1"></i>Auto-Complete Enabled</div><div class="vt-rt-body">This pull request will merge automatically as soon as all branch policies and required approvals are satisfied.</div></div>';
        },
        readyToMergeTooltipHtml() {
            return '<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-check2-all text-success mr-1"></i>Ready to Merge</div><div class="vt-rt-body">All required reviewers have approved, discussion threads are resolved, and branch policies are satisfied.</div></div>';
        },
        mergeConflictsTooltipHtml() {
            return '<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-exclamation-triangle-fill text-danger mr-1"></i>Merge Conflicts</div><div class="vt-rt-body">Source and target branches have conflicting changes that must be resolved before merging.</div></div>';
        },
        mergedWiOpenTooltipHtml() {
            return '<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-card-checklist text-warning mr-1"></i>Open Work Items After Merge</div><div class="vt-rt-body">This pull request is already merged, but one or more linked work items are still open. Click the transition button in the Work Items column to close them.</div></div>';
        }
    },
    methods: {
        loadStoredPrFeatureSettings() {
            const defaults = {
                enabled: true,
                showUpdatedSinceReview: true,
                showReadyToMerge: true,
                highlightConflicts: true,
                showQualityGates: true,
                showCommentGroups: true,
                showReviewerVotes: true,
                showCiFailed: true,
                showMergedWorkItemOpen: true,
                showReviewerOnPto: true
            };
            try {
                if (typeof localStorage !== 'undefined') {
                    const raw = localStorage.getItem('vt_pr_feature_config');
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        if (parsed && typeof parsed === 'object') {
                            return {
                                enabled: parsed.enabled !== false,
                                showUpdatedSinceReview: parsed.showUpdatedSinceReview !== false,
                                showReadyToMerge: parsed.showReadyToMerge !== false,
                                highlightConflicts: parsed.highlightConflicts !== false,
                                showQualityGates: parsed.showQualityGates !== false,
                                showCommentGroups: parsed.showCommentGroups !== false,
                                showReviewerVotes: parsed.showReviewerVotes !== false,
                                showCiFailed: parsed.showCiFailed !== false,
                                showMergedWorkItemOpen: parsed.showMergedWorkItemOpen !== false,
                                showReviewerOnPto: parsed.showReviewerOnPto !== false
                            };
                        }
                    }
                }
            } catch (e) {
                // ignore
            }
            return defaults;
        },
        onPrFeatureSettingsSaved(newSettings) {
            if (!newSettings || typeof newSettings !== 'object') return;
            this.prFeatureSettings = {
                enabled: newSettings.enabled !== false,
                showUpdatedSinceReview: newSettings.showUpdatedSinceReview !== false,
                showReadyToMerge: newSettings.showReadyToMerge !== false,
                highlightConflicts: newSettings.highlightConflicts !== false,
                showQualityGates: newSettings.showQualityGates !== false,
                showCommentGroups: newSettings.showCommentGroups !== false,
                showReviewerVotes: newSettings.showReviewerVotes !== false,
                showCiFailed: newSettings.showCiFailed !== false,
                showMergedWorkItemOpen: newSettings.showMergedWorkItemOpen !== false,
                showReviewerOnPto: newSettings.showReviewerOnPto !== false
            };
            if (this.prFeatureSettings.enabled === false) {
                this.onlyAttentionPrs = false;
            }
        },
        escapeTooltipHtml(val) {
            return String(val == null ? '' : val)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },
        isCurrentUserReviewerName(name) {
            if (!name || typeof name !== 'string') return false;
            const target = name.trim().toLowerCase();
            if (!target) return false;
            const tokens = new Set();
            const addToken = (val) => {
                if (val && typeof val === 'string' && val.trim()) {
                    tokens.add(val.trim().toLowerCase());
                }
            };
            addToken(this.userName);
            addToken(this.$store.state.account && this.$store.state.account.fullName);
            addToken(this.$store.state.account && this.$store.state.account.email);
            const projKey = (this.activeProject && this.activeProject.id) || 'default';
            const members = (this.$store.state.teamMembersByProject && this.$store.state.teamMembersByProject[projKey]) || [];
            const meMember = members.find(m => m && m.isCurrentUser);
            if (meMember) {
                addToken(meMember.displayName);
                addToken(meMember.uniqueName);
                addToken(meMember.id);
            }
            return tokens.has(target);
        },
        isAwaitingMyReview(pr) {
            if (!pr || pr.status !== 'Active' || pr.isDraft) return false;
            const cfg = this.prFeatureSettings;
            if (cfg && cfg.enabled === false) return false;
            if (pr.isCreatedByMe || this.isCurrentUserReviewerName(pr.createdByName || pr.author)) return false;
            const reviewers = Array.isArray(pr.reviewers) ? pr.reviewers : [];
            return reviewers.some(r => r && this.isCurrentUserReviewerName(r.name) && Number(r.vote || 0) === 0);
        },
        awaitingMyReviewTooltipHtml(pr) {
            const author = this.escapeTooltipHtml((pr && (pr.createdByName || pr.author)) || 'Author');
            const meRev = (pr && Array.isArray(pr.reviewers) ? pr.reviewers : []).find(r => r && this.isCurrentUserReviewerName(r.name));
            const roleLabel = meRev && meRev.isRequired ? 'Required reviewer' : 'Assigned reviewer';
            return `<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-person-check-fill text-primary mr-1"></i>Awaiting Your Review</div><div class="vt-rt-body"><strong>${author}</strong> is waiting for your review vote (<span class="vt-rt-tag">${roleLabel}</span>).</div></div>`;
        },
        isPrUpdatedSinceReview(pr) {
            if (!pr || pr.status !== 'Active' || pr.isDraft) return false;
            const cfg = this.prFeatureSettings;
            if (cfg && (cfg.enabled === false || cfg.showUpdatedSinceReview === false)) return false;
            return !!pr.hasUpdatesSinceReview;
        },
        isCurrentUserWaitingReviewer(pr) {
            if (!pr || !Array.isArray(pr.waitingReviewerNames) || !pr.waitingReviewerNames.length) return false;
            return pr.waitingReviewerNames.some(n => this.isCurrentUserReviewerName(n));
        },
        updatedSinceReviewTooltip(pr) {
            const names = Array.isArray(pr?.waitingReviewerNames) && pr.waitingReviewerNames.length
                ? pr.waitingReviewerNames.map(n => this.escapeTooltipHtml(n)).join(', ')
                : 'reviewer';
            if (this.isCurrentUserWaitingReviewer(pr)) {
                return '<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-arrow-repeat text-info mr-1"></i>Updated Since Your Review</div><div class="vt-rt-body">Author pushed new commits after your review vote — ready for your re-review!</div></div>';
            }
            return `<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-arrow-repeat text-info mr-1"></i>Updated Since Review</div><div class="vt-rt-body">Author pushed new commits after review feedback — ball is back in <strong>${names}</strong>&#39;s court for re-review.</div></div>`;
        },
        hasActiveCommentThreads(pr) {
            if (!pr || !Array.isArray(pr.commentGroups)) return false;
            return pr.commentGroups.some(g => g && g.isActive && g.count > 0);
        },
        hasAllRequiredApprovals(pr) {
            if (!pr || !Array.isArray(pr.reviewers) || !pr.reviewers.length) return false;
            if (pr.reviewers.some(r => Number(r.vote) < 0)) return false;
            const req = pr.reviewers.filter(r => r.isRequired);
            if (req.length > 0) {
                return req.every(r => Number(r.vote) >= 5);
            }
            return pr.reviewers.every(r => Number(r.vote) >= 5);
        },
        effectiveBlockerReasons(pr) {
            if (!pr || !pr.isBlocked) return [];
            const reasons = Array.isArray(pr.blockerReasons) ? pr.blockerReasons : [];
            if (!reasons.length) return pr.hasConflicts ? [] : ['Blocked by quality gate or policies'];
            const approvalsMet = this.hasAllRequiredApprovals(pr);
            const noActiveThreads = !this.hasActiveCommentThreads(pr);
            const reviewers = Array.isArray(pr.reviewers) ? pr.reviewers : [];
            const hasNegativeVote = reviewers.some(r => r && Number(r.vote) < 0);
            return reasons.filter(r => {
                const lower = String(r || '').toLowerCase().trim();
                if (lower.includes('conflict')) return false;
                if (noActiveThreads && lower.includes('comment')) return false;
                if (lower.includes('required approval missing')) {
                    if (approvalsMet || !hasNegativeVote) return false;
                    const prefix = 'required approval missing from ';
                    if (lower.startsWith(prefix)) {
                        const targetName = lower.slice(prefix.length).trim();
                        const matchedRev = reviewers.find(rv => rv && rv.name && rv.name.trim().toLowerCase() === targetName);
                        if (matchedRev && Number(matchedRev.vote || 0) >= 0) return false;
                    }
                }
                if ((approvalsMet || !hasNegativeVote) && noActiveThreads && !pr.ciFailed && lower === 'rejected by branch policy') return false;
                return true;
            });
        },
        isPrEffectivelyBlocked(pr) {
            return this.effectiveBlockerReasons(pr).length > 0;
        },
        isPrConflictSignalVisible(pr) {
            if (!pr || pr.status !== 'Active' || pr.isDraft) return false;
            const cfg = this.prFeatureSettings;
            if (cfg && (cfg.enabled === false || cfg.highlightConflicts === false)) return false;
            return !!pr.hasConflicts || this.isPrEffectivelyBlocked(pr);
        },
        hasVisiblePrFlags(pr) {
            if (!pr) return false;
            if (pr.hasConflicts) return true;
            if (this.isPrEffectivelyBlocked(pr) || pr.qualityGateStatus === 'Passed' || pr.qualityGateStatus === 'Pending') return true;
            if (Array.isArray(pr.commentGroups) && pr.commentGroups.length > 0) return true;
            return false;
        },
        hasVisiblePrFlagsLegacy(pr) {
            if (!pr) return false;
            const cfg = this.prFeatureSettings;
            const masterOn = !cfg || cfg.enabled !== false;
            const showConflicts = masterOn && (!cfg || cfg.highlightConflicts !== false);
            const showGates = masterOn && (!cfg || cfg.showQualityGates !== false);
            const showComments = masterOn && (!cfg || cfg.showCommentGroups !== false);
            if (showConflicts && pr.hasConflicts) return true;
            if (showGates && (this.isPrEffectivelyBlocked(pr) || pr.qualityGateStatus === 'Passed' || pr.qualityGateStatus === 'Pending')) return true;
            if (showComments && Array.isArray(pr.commentGroups) && pr.commentGroups.length > 0) return true;
            return false;
        },
        isPrReadyToMerge(pr) {
            if (!pr || pr.status !== 'Active' || pr.isDraft) return false;
            const cfg = this.prFeatureSettings;
            if (cfg && (cfg.enabled === false || cfg.showReadyToMerge === false)) return false;
            if (pr.hasConflicts || pr.hasAutoComplete) return false;
            if (this.isPrEffectivelyBlocked(pr)) return false;
            if (this.hasActiveCommentThreads(pr)) return false;
            return !!pr.isReadyToMerge || this.hasAllRequiredApprovals(pr);
        },
        hasVisiblePrRowBadges(pr) {
            return this.isAwaitingMyReview(pr) || this.isPrUpdatedSinceReview(pr) || this.isPrReadyToMerge(pr) || this.isPrAutoComplete(pr) || this.isPrConflictSignalVisible(pr) || this.isPrCiFailed(pr) || this.isPrMergedWorkItemOpen(pr) || this.isPrReviewerOnPto(pr) || this.isPrActiveThreadsVisible(pr);
        },
        activeThreadsTooltipHtml(pr) {
            const count = this.activeThreadCount(pr);
            const label = count === 1 ? '1 unresolved discussion comment' : `${count} unresolved discussion comments`;
            return `<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-chat-left-text-fill text-warning mr-1"></i>Active Discussion Threads</div><div class="vt-rt-body"><strong>${label}</strong> require resolution. Click to open the discussion modal.</div></div>`;
        },
        activeThreadCount(pr) {
            if (!pr || !Array.isArray(pr.commentGroups)) return 0;
            return pr.commentGroups
                .filter(g => g && (g.isActive || g.status === 'Active' || g.status === 'Pending') && Number(g.count) > 0)
                .reduce((sum, g) => sum + Number(g.count || 0), 0);
        },
        isPrActiveThreadsVisible(pr) {
            if (!pr || pr.status === 'Abandoned') return false;
            const cfg = this.prFeatureSettings;
            if (cfg && (cfg.enabled === false || cfg.showCommentGroups === false)) return false;
            return this.activeThreadCount(pr) > 0;
        },
        openActiveComments(pr) {
            if (!pr || !Array.isArray(pr.commentGroups)) return;
            const activeGroup = pr.commentGroups.find(g => g && (g.isActive || g.status === 'Active' || g.status === 'Pending') && Number(g.count) > 0)
                || pr.commentGroups[0];
            if (activeGroup) {
                this.openComments(pr, activeGroup);
            }
        },
        loadReviewerPtoAsync() {
            const from = new Date();
            const to = new Date(from.getTime() + 3 * 24 * 60 * 60 * 1000);
            return this.loadPtoEntriesAsync(from, to);
        },
        reviewersOnPto(pr) {
            if (!pr || pr.status !== 'Active' || pr.isDraft || !Array.isArray(pr.reviewers)) return [];
            const cfg = this.prFeatureSettings;
            if (cfg && (cfg.enabled === false || cfg.showReviewerOnPto === false)) return [];
            return pr.reviewers
                .filter(r => r && r.name && !r.vote)
                .map(r => ({ name: r.name, pto: this.getPersonPto(r.name, '') }))
                .filter(r => r.pto);
        },
        isPrReviewerOnPto(pr) {
            return this.reviewersOnPto(pr).length > 0;
        },
        reviewerOnPtoTooltip(pr) {
            const items = this.reviewersOnPto(pr)
                .map(r => `<li><strong>${this.escapeTooltipHtml(r.name)}:</strong> ${this.escapeTooltipHtml(r.pto && r.pto.tooltip ? r.pto.tooltip : 'Out of Office')}</li>`)
                .join('');
            return `<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-airplane-fill text-warning mr-1"></i>Reviewer Out of Office</div><div class="vt-rt-body"><ul class="vt-rt-list">${items}</ul><div class="mt-1">Consider assigning an additional reviewer.</div></div></div>`;
        },
        isPrCiFailed(pr) {
            if (!pr || pr.status !== 'Active' || pr.isDraft) return false;
            const cfg = this.prFeatureSettings;
            if (cfg && (cfg.enabled === false || cfg.showCiFailed === false)) return false;
            return !!pr.ciFailed;
        },
        ciFailedTooltip(pr) {
            const summary = pr && pr.ciFailureSummary
                ? this.escapeTooltipHtml(pr.ciFailureSummary)
                : 'The latest CI build validation failed.';
            return `<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-x-octagon-fill text-danger mr-1"></i>CI Build Failed</div><div class="vt-rt-body">${summary}</div></div>`;
        },
        openMergedWorkItems(pr) {
            if (!pr || !Array.isArray(pr.workItems)) return [];
            return pr.workItems.filter(w => w && w.isOpenAfterMerge);
        },
        isPrMergedWorkItemOpen(pr) {
            if (!pr || pr.status !== 'Completed') return false;
            const cfg = this.prFeatureSettings;
            if (cfg && (cfg.enabled === false || cfg.showMergedWorkItemOpen === false)) return false;
            return !!pr.isMergedWithOpenWorkItems && this.openMergedWorkItems(pr).length > 0;
        },
        async resolveMergedWorkItemAsync(pr, wi) {
            if (!wi || !wi.suggestedResolveState || this.resolvingWorkItemIds[wi.id]) return;
            this.$set(this.resolvingWorkItemIds, wi.id, true);
            try {
                const formData = new FormData();
                formData.append('workItemId', String(wi.id));
                formData.append('state', wi.suggestedResolveState);
                const endpoint = wi.isPlannable ? '/TaskManagement/ChangeTaskItemState' : '/TaskManagement/ChangeUserStoryItemState';
                await this.$http.patch(endpoint, formData);
                this.$set(wi, 'state', wi.suggestedResolveState);
                this.$set(wi, 'isOpenAfterMerge', false);
                this.toastSuccess(`Moved #${wi.id} to '${wi.suggestedResolveState}'.`);
            } catch (err) {
                const msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Could not change the work item state.';
                this.toastError('Failed to update work item', msg);
            } finally {
                this.$delete(this.resolvingWorkItemIds, wi.id);
            }
        },
        isPrAutoComplete(pr) {
            if (!pr || pr.status !== 'Active' || pr.isDraft) return false;
            return !!pr.hasAutoComplete;
        },
        normalizeFilterList(val, fallback = 'all') {
            if (Array.isArray(val)) {
                if (val.length > 1 && val.every(ch => typeof ch === 'string' && ch.length === 1)) {
                    return [val.join('')];
                }
                return val.length ? val : [fallback];
            }
            if (typeof val === 'string' && val.trim()) {
                return [val.trim()];
            }
            return [fallback];
        },
        matchesAppliedReviewerFilter(pr) {
            const revList = this.normalizeFilterList(this.appliedReviewers, 'all');
            const revKey = String(revList[0] || 'all').toLowerCase();
            if (!revKey || revKey === 'all' || revKey === 'anyone') return true;

            if (!revKey || revKey === 'all') return true;
            const reviewers = (pr && Array.isArray(pr.reviewers)) ? pr.reviewers : [];
            if (!reviewers.length) return false;
            const projKey = (this.activeProject && this.activeProject.id) || 'default';
            const members = (this.$store.state.teamMembersByProject && this.$store.state.teamMembersByProject[projKey]) || [];
            const tokens = new Set();
            const addToken = (val) => {
                if (val && typeof val === 'string' && val.trim()) {
                    tokens.add(val.trim().toLowerCase());
                }
            };
            if (revKey === 'me') {
                addToken(this.userName);
                addToken(this.$store.state.account && this.$store.state.account.fullName);
                addToken(this.$store.state.account && this.$store.state.account.email);
                const meMember = members.find(m => m && m.isCurrentUser);
                if (meMember) {
                    addToken(meMember.displayName);
                    addToken(meMember.uniqueName);
                    addToken(meMember.id);
                }
            } else if (revKey === 'team') {
                if (!members.length) return reviewers.length > 0;
                members.forEach(m => {
                    if (!m) return;
                    addToken(m.displayName);
                    addToken(m.uniqueName);
                    addToken(m.id);
                });
            } else {
                addToken(revKey);
                members.forEach(m => {
                    if (!m) return;
                    const mKey = (m.uniqueName || m.id || m.displayName || '').toLowerCase();
                    if (mKey === String(revKey).toLowerCase()
                        || (m.displayName && m.displayName.toLowerCase() === String(revKey).toLowerCase())
                        || (m.uniqueName && m.uniqueName.toLowerCase() === String(revKey).toLowerCase())
                        || (m.id && String(m.id).toLowerCase() === String(revKey).toLowerCase())) {
                        addToken(m.displayName);
                        addToken(m.uniqueName);
                        addToken(m.id);
                    }
                });
            }
            if (!tokens.size) return true;
            return reviewers.some(r => r && r.name && tokens.has(String(r.name).trim().toLowerCase()));
        },
        hasPrAttentionSignal(pr) {
            if (!pr) return false;
            const cfg = this.prFeatureSettings;
            if (cfg && cfg.enabled === false) return false;
            const conflictOrBlocked = (!cfg || cfg.highlightConflicts !== false) && (this.isPrEffectivelyBlocked(pr) || !!pr.hasConflicts);
            return conflictOrBlocked || this.isAwaitingMyReview(pr) || this.isPrUpdatedSinceReview(pr) || this.isPrReadyToMerge(pr) || this.isPrCiFailed(pr) || this.isPrMergedWorkItemOpen(pr) || this.isPrReviewerOnPto(pr) || this.isPrActiveThreadsVisible(pr);
        },
        loadStoredPrColumns() {
            try {
                if (typeof localStorage !== 'undefined') {
                    const raw = localStorage.getItem('vt_pr_columns_config');
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            return parsed.map((c, idx) => {
                                const def = DEFAULT_PR_COLUMNS.find(d => d.referenceName === (c.referenceName || c.key)) || {};
                                return {
                                    ...def,
                                    ...c,
                                    key: c.key || c.referenceName || def.key,
                                    referenceName: c.referenceName || c.key || def.referenceName,
                                    width: ((c.referenceName || c.key) === 'buttons' && c.width === '4.5rem') ? '6.75rem' : (c.width || def.width || 'minmax(6rem, 1fr)'),
                                    order: c.order || idx + 1,
                                    isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                                    hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false
                                };
                            });
                        }
                    }
                }
            } catch (e) {
                // ignore
            }
            return DEFAULT_PR_COLUMNS.map(c => ({
                ...c,
                allowedValues: c.allowedValues ? [...c.allowedValues] : [],
                valueColors: c.valueColors ? { ...c.valueColors } : {}
            }));
        },
        openColumnSettings() {
            if (this.$refs.modalPrColumns) {
                this.$refs.modalPrColumns.showModal();
            }
        },
        onColumnsSaved(newColumns) {
            if (Array.isArray(newColumns) && newColumns.length > 0) {
                this.activeGridColumns = newColumns.map((c, idx) => {
                    const def = DEFAULT_PR_COLUMNS.find(d => d.referenceName === (c.referenceName || c.key)) || {};
                    return {
                        ...def,
                        ...c,
                        key: c.key || c.referenceName || def.key,
                        referenceName: c.referenceName || c.key || def.referenceName,
                        width: c.width || def.width || 'minmax(6rem, 1fr)',
                        order: c.order || idx + 1,
                        isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                        hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false
                    };
                });
            }
        },
        prStatusColor(statusName) {
            if (!statusName) return '';
            const statusCol = (this.activeGridColumns || []).find(c => (c.referenceName || c.key) === 'status');
            if (statusCol && statusCol.valueColors && typeof statusCol.valueColors === 'object') {
                if (statusCol.valueColors[statusName]) return statusCol.valueColors[statusName];
                const match = Object.entries(statusCol.valueColors).find(([k]) => k.toLowerCase() === statusName.toLowerCase());
                if (match) return match[1];
            }
            return DEFAULT_PR_STATUS_COLORS[statusName] || '';
        },
        getPrAdoUrl(pr) {
            if (!pr) return null;
            if (pr.url && pr.url !== "#") return pr.url;
            const project = pr.projectName || this.globalProject?.name || "core";
            const repo = pr.repositoryName || "tailspin-scrum";
            if (pr.id) {
                return `https://dev.azure.com/velocytrack/${encodeURIComponent(project)}/_git/${encodeURIComponent(repo)}/pullrequest/${pr.id}`;
            }
            return null;
        },
        getWorkItemAdoUrl(wi, pr) {
            if (!wi) return null;
            if (wi.url && wi.url !== "#") return wi.url;
            const project = (pr && pr.projectName) || this.globalProject?.name || "core";
            if (wi.id) {
                return `https://dev.azure.com/velocytrack/${encodeURIComponent(project)}/_workitems/edit/${wi.id}`;
            }
            return null;
        },
        selectAllStatuses() {
            this.statusFilter = [...this.statusOptions];
        },
        toggleStatusFilter(opt, isChecked) {
            if (isChecked) {
                if (!this.statusFilter.includes(opt)) {
                    this.statusFilter = [...this.statusFilter, opt];
                }
            } else {
                if (this.statusFilter.length <= 1) return;
                this.statusFilter = this.statusFilter.filter(s => s !== opt);
            }
        },
        applyPrFilters() {
            if (this.isStatusFilterInvalid) return;
            this.appliedAuthors = this.normalizeFilterList(this.selectedAuthors, 'me');
            this.appliedStatusFilter = [...(this.statusFilter || [])];
            this.appliedReviewers = [...(Array.isArray(this.selectedReviewers) ? this.selectedReviewers : [this.selectedReviewers || 'all'])];
            this.appliedShowOptionalReviewers = this.showOptionalReviewers;
            this.appliedShowUnassigned = this.showUnassigned;
            this.$store.commit("setLastPrStatusFilter", this.statusFilter);
            this.$store.commit("setLastPrShowOptionalReviewers", this.showOptionalReviewers);
            this.$store.commit("setLastPrShowUnassigned", this.showUnassigned);
            this.loadPullRequestsAsync();
        },
        resetAllPrFilters() {
            this.onlyAttentionPrs = false;
            this.prSearchQuery = '';
            this.selectedAuthors = ["me"];
            this.statusFilter = ["Active"];
            this.showUnassigned = false;
            this.selectedReviewers = ["all"];
            this.showOptionalReviewers = false;
            this.applyPrFilters();
        },
        onEmptyAction(actionKey) {
            if (actionKey === 'author-team') {
                this.selectedAuthors = ['team'];
                this.applyPrFilters();
            } else if (actionKey === 'author-all') {
                this.selectedAuthors = ['all'];
                this.applyPrFilters();
            } else if (actionKey === 'all-statuses') {
                this.selectAllStatuses();
                this.applyPrFilters();
            } else if (actionKey === 'toggle-workitems') {
                this.showUnassigned = true;
                this.applyPrFilters();
            } else if (actionKey === 'reviewers-all' || actionKey === 'reviewers-required') {
                this.selectedReviewers = ['all'];

            this.showOptionalReviewers = false;
                this.applyPrFilters();
            }
        },
        pinStatusFilter(event) {
            if (this.statusFilterOpen) return;
            this.pinStatusFilterEl(event.currentTarget);
        },
        unpinStatusFilter(event) {
            if (this.statusFilterOpen) return;
            this.unpinStatusFilterEl(event.currentTarget);
        },
        onStatusFilterShown() {
            this.statusFilterOpen = true;
            this.pinStatusFilterEl();
        },
        onStatusFilterHidden() {
            this.statusFilterOpen = false;
            this.unpinStatusFilterEl();
        },
        pinStatusFilterEl(wrapArg) {
            const wrap = wrapArg instanceof Element ? wrapArg : this.$el.querySelector(".pr-status-filter-wrap");
            const toggle = wrap ? wrap.querySelector(".dropdown-toggle") : null;
            if (!toggle) return;
            const rect = toggle.getBoundingClientRect();
            toggle.classList.add("is-pinned");
            toggle.style.top = `${rect.top}px`;
            toggle.style.left = `${rect.left}px`;
        },
        unpinStatusFilterEl(wrapArg) {
            const wrap = wrapArg instanceof Element ? wrapArg : this.$el.querySelector(".pr-status-filter-wrap");
            const toggle = wrap ? wrap.querySelector(".dropdown-toggle") : null;
            if (!toggle) return;
            toggle.classList.remove("is-pinned");
            toggle.style.top = "";
            toggle.style.left = "";
        },
        isRowLocked(pr) {
            if (this.isViewOnlyMode) return true;
            return this.isAnyRowModifying && !this.isTitleDirty(pr);
        },
        isRowOverflowing(pr) {
            const workItems = (pr.workItems && pr.workItems.length) || 0;
            const reviewers = (pr.reviewers && pr.reviewers.length) || 0;
            return Math.max(workItems, reviewers) > 3;
        },
        onSprintsLoaded(sprints) {
            this.hasSprints = Array.isArray(sprints) && sprints.length > 0;
        },
        async onSprintPicked(sprintPath) {
            if (!this.hasRepoSource) {
                this.pullRequests = [];
                return;
            }
            if (sprintPath) {
                this.hasSprints = true;
            }
            const prevSprint = this.selectedSprint;
            this.selectedSprint = sprintPath;
            try {
                await this.loadPullRequestsAsync();
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) {
                    this.selectedSprint = prevSprint;
                }
            }
        },
        openUpgradeModal(plan = 'Pro Plan') {
            this.$router.push({
                path: '/plans',
                query: {
                    reason: 'Pull Requests',
                    requiredPlan: plan
                }
            }).catch(() => { });

        },
        async loadPullRequestsAsync() {
            if (!this.hasRepoSource) {
                this.pullRequests = [];
                this.loadingPullRequests = false;
                return;
            }
            if (this.prAbortController) {
                this.prAbortController.abort();
                this.prAbortController = null;
            }
            const controller = new AbortController();
            this.prAbortController = controller;

            this.loadingPullRequests = true;
            this.appliedAuthors = this.normalizeFilterList(this.selectedAuthors, 'me');
            this.appliedStatusFilter = [...(this.statusFilter || [])];
            this.appliedReviewers = [...(Array.isArray(this.selectedReviewers) ? this.selectedReviewers : [this.selectedReviewers || 'all'])];
            this.appliedShowOptionalReviewers = this.showOptionalReviewers;
            this.appliedShowUnassigned = this.showUnassigned;
            try {
                const params = {};
                if (this.activeProject?.id) params.projectId = this.activeProject.id;
                if (this.selectedSprint && this.hasBacklogConnection) params.sprint = this.selectedSprint;
                if (this.showUnassigned || !this.hasBacklogConnection) params.includeUnassigned = true;
                params.statuses = (this.statusFilter && this.statusFilter.length) ? this.statusFilter.join(',') : 'none';
                const authList = this.normalizeFilterList(this.selectedAuthors, 'me');
                if (authList.length) params.authors = authList.join(',');

                const revList = this.normalizeFilterList(this.selectedReviewers, 'all');
                if (revList.length && revList[0] && revList[0] !== 'all' && revList[0] !== 'anyone') params.reviewers = revList.join(',');
                params._ = Date.now();

                const resp = await this.$http.get("/Azure/ListPullRequests", {
                    params,
                    signal: controller.signal
                });
                const data = resp.data ?? [];
                data.forEach(pr => {
                    pr.titleDraft = pr.title;
                    this.$set(pr, "savingTitle", false);
                    pr.expanded = false;
                });
                this.pullRequests = data;
                this.loadReviewerPtoAsync();
                this.warmPrRepositoriesBranchCache(data);
                this.collapsesStates = [];
            } catch (err) {
                if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
                    return;
                }
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) {
                    return;
                }
                if (err?.isNetworkError || (!err?.response?.status && !err?.status)) {
                    this.pullRequests = [];
                    return;
                }
                console.error("Failed to load pull requests", err);
                this.toastError("Failed to load pull requests");
                this.pullRequests = [];
            } finally {
                if (this.prAbortController === controller) {
                    this.loadingPullRequests = false;
                    this.prAbortController = null;
                }
            }
        },
        actionHoverPop(title) {
            return { title, boundary: "viewport", delay: { show: 300, hide: 0 } };
        },
        blockerTooltip(pr) {
            if (!pr || !pr.isBlocked) {
                return '<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-shield-fill-x text-danger mr-1"></i>Quality Gate Blocked</div><div class="vt-rt-body">Pull request is blocked by branch policies or quality gates.</div></div>';
            }
            const reasons = this.effectiveBlockerReasons(pr);
            if (reasons.length) {
                const items = reasons.map(r => `<li>${this.escapeTooltipHtml(r)}</li>`).join('');
                return `<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-shield-fill-x text-danger mr-1"></i>Pull Request Blocked</div><div class="vt-rt-body"><ul class="vt-rt-list">${items}</ul></div></div>`;
            }
            return '<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-shield-fill-x text-danger mr-1"></i>Pull Request Blocked</div><div class="vt-rt-body">Blocked by quality gate or branch policies.</div></div>';
        },
        commentVariant(status) {
            switch (status) {
                case "Active": return "danger";
                case "Pending": return "warning";
                case "Fixed": return "success";
                case "ByDesign": return "info";
                case "WontFix":
                case "Won't Fix": return "warning";
                case "Closed": return "secondary";
                default: return "light";
            }
        },
        commentClass(group) {
            return `text-${this.commentVariant(group.status)}`;
        },
        renderComment(text) {
            if (!text) return "";
            const emojiMap = {
                large_orange_diamond: "🔶",
                small_orange_diamond: "🔸",
                large_blue_diamond: "🔷",
                small_blue_diamond: "🔹",
                red_circle: "🔴",
                large_red_square: "🟥",
                warning: "⚠️",
                page_facing_up: "📄",
                point_right: "👉",
                white_check_mark: "✅",
                heavy_check_mark: "✔️",
                x: "❌",
                bulb: "💡",
                information_source: "ℹ️",
                memo: "📝",
                pencil: "✏️"
            };
            let html = text
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
            html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
                '<a href="$2" target="_blank" rel="noopener">$1</a>');
            html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
            html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
            html = html.replace(/:([a-z0-9_+-]+):/g, (m, name) => emojiMap[name] || m);
            html = html.replace(/\n+/g, "<br>");
            return DOMPurify.sanitize(html, {
                ALLOWED_TAGS: ["a", "strong", "code", "br"],
                ALLOWED_ATTR: ["href", "target", "rel"]
            });
        },
        commentStatusLabel(status) {
            switch (status) {
                case "Fixed": return "Resolved";
                case "WontFix": return "Won't Fix";
                case "ByDesign": return "By Design";
                default: return status || "Active";
            }
        },
        getThreadTransitions(comment) {
            if (comment && Array.isArray(comment.allowedTransitions) && comment.allowedTransitions.length > 0) {
                return comment.allowedTransitions;
            }
            const platform = (comment && comment.platform) || "AzureDevOps";
            const status = (comment && comment.status) || "Active";
            if (platform.toLowerCase() !== "azuredevops") {
                const isResolved = status === "Fixed" || status === "Resolved" || status === "Closed";
                return isResolved
                    ? [{ actionKey: "reactivate", label: "Reactivate", targetStatus: "Active", variant: "outline-primary" }]
                    : [{ actionKey: "resolve", label: "Resolve", targetStatus: "Fixed", variant: "outline-success" }];
            }
            if (status === "Active") {
                return [
                    { actionKey: "resolve", label: "Resolve", targetStatus: "Fixed", variant: "outline-success" },
                    { actionKey: "wontfix", label: "Won't Fix", targetStatus: "WontFix", variant: "outline-warning" },
                    { actionKey: "close", label: "Close", targetStatus: "Closed", variant: "outline-secondary" }
                ];
            }
            if (status === "Fixed" || status === "Resolved" || status === "WontFix" || status === "ByDesign") {
                return [
                    { actionKey: "reactivate", label: "Reactivate", targetStatus: "Active", variant: "outline-primary" },
                    { actionKey: "close", label: "Close", targetStatus: "Closed", variant: "outline-secondary" }
                ];
            }
            if (status === "Closed") {
                return [
                    { actionKey: "reactivate", label: "Reactivate", targetStatus: "Active", variant: "outline-primary" }
                ];
            }
            return [
                { actionKey: "activate", label: "Activate", targetStatus: "Active", variant: "outline-primary" },
                { actionKey: "resolve", label: "Resolve", targetStatus: "Fixed", variant: "outline-success" },
                { actionKey: "wontfix", label: "Won't Fix", targetStatus: "WontFix", variant: "outline-warning" },
                { actionKey: "close", label: "Close", targetStatus: "Closed", variant: "outline-secondary" }
            ];
        },
        decorateReplyItem(r, rIdx) {
            return Object.assign({
                commentId: (r && r.commentId) || (rIdx + 1),
                authorName: (r && r.authorName) || "",
                publishedDate: (r && r.publishedDate) || null,
                text: (r && r.text) || "",
                editing: false,
                editDraft: "",
                saving: false,
                deleting: false
            }, r, {
                commentId: (r && r.commentId) || (rIdx + 1),
                editing: Boolean(r && r.editing),
                editDraft: (r && r.editDraft) || "",
                saving: Boolean(r && r.saving),
                deleting: Boolean(r && r.deleting)
            });
        },
        decorateCommentThread(c, idx) {
            const replies = Array.isArray(c.replies) ? c.replies.map((r, rIdx) => this.decorateReplyItem(r, rIdx)) : [];
            return Object.assign({
                threadId: c.threadId || (idx + 1),
                commentId: c.commentId || (idx + 1),
                authorName: c.authorName || "",
                publishedDate: c.publishedDate || null,
                platform: c.platform || "AzureDevOps",
                allowedTransitions: Array.isArray(c.allowedTransitions) ? c.allowedTransitions : [],
                replies,
                replyFocused: false,
                replyDraft: "",
                submittingReply: false,
                updatingStatus: false
            }, c, {
                threadId: c.threadId || (idx + 1),
                replies,
                replyFocused: Boolean(c.replyFocused),
                replyDraft: c.replyDraft || "",
                submittingReply: Boolean(c.submittingReply),
                updatingStatus: Boolean(c.updatingStatus)
            });
        },
        async openComments(pr, group) {
            this.expandedPr = pr;
            this.expandedGroupStatus = group.status;
            this.expandedTitle = `PR !${pr.id} – ${this.commentStatusLabel(group.status)} comments`;
            this.allPrComments = [];
            this.expandedComments = [];
            this.commentsPage = 1;
            this.loadingComments = true;
            this.showCommentsModal = true;
            try {
                const resp = await this.$http.get("/Azure/ListPullRequestComments", {
                    params: { repositoryId: pr.repositoryId, pullRequestId: pr.id }
                });
                const all = (resp.data ?? []).map((c, idx) => this.decorateCommentThread(c, idx));
                this.allPrComments = all;
                this.expandedComments = all.filter(c => c.status === group.status);
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) {
                    this.expandedComments = [];
                    return;
                }
                console.error("Failed to load comments", err);
                this.toastError("Failed to load comments");
                this.expandedComments = [];
            } finally {
                this.loadingComments = false;
            }
        },
        onReplyFocus(comment) {
            this.$set(comment, "replyFocused", true);
        },
        onReplyBlur(comment) {
            this.$set(comment, "replyFocused", false);
        },
        startEditThreadReply(reply) {
            if (!reply) return;
            this.$set(reply, "editDraft", reply.text || "");
            this.$set(reply, "editing", true);
        },
        cancelEditThreadReply(reply) {
            if (!reply) return;
            this.$set(reply, "editing", false);
            this.$set(reply, "editDraft", "");
        },
        async saveEditThreadReply(comment, reply) {
            if (!comment || !reply || !this.expandedPr) return;
            const text = (reply.editDraft || "").trim();
            if (!text) return;

            this.$set(reply, "saving", true);
            try {
                const resp = await this.$http.post("/Azure/UpdatePullRequestThreadReply", {
                    repositoryId: this.expandedPr.repositoryId,
                    pullRequestId: this.expandedPr.id,
                    threadId: comment.threadId || 1,
                    commentId: reply.commentId,
                    text
                });
                const data = resp?.data;
                if (data && Array.isArray(data.replies)) {
                    this.$set(comment, "replies", data.replies.map((r, rIdx) => this.decorateReplyItem(r, rIdx)));
                } else {
                    this.$set(reply, "text", text);
                    this.$set(reply, "editing", false);
                    this.$set(reply, "editDraft", "");
                }
                this.toastSuccess("Reply updated", `PR !${this.expandedPr.id}`);
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                console.error("Failed to update PR thread reply", err);
                this.toastError("Failed to update reply");
            } finally {
                this.$set(reply, "saving", false);
            }
        },
        async deleteThreadReply(comment, reply) {
            if (!comment || !reply || !this.expandedPr) return;

            this.$set(reply, "deleting", true);
            try {
                const resp = await this.$http.post("/Azure/DeletePullRequestThreadReply", {
                    repositoryId: this.expandedPr.repositoryId,
                    pullRequestId: this.expandedPr.id,
                    threadId: comment.threadId || 1,
                    commentId: reply.commentId
                });
                const data = resp?.data;
                if (data && Array.isArray(data.replies)) {
                    this.$set(comment, "replies", data.replies.map((r, rIdx) => this.decorateReplyItem(r, rIdx)));
                } else {
                    const remaining = (comment.replies || []).filter(r => r.commentId !== reply.commentId);
                    this.$set(comment, "replies", remaining);
                }
                this.toastSuccess("Reply deleted", `PR !${this.expandedPr.id}`);
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                console.error("Failed to delete PR thread reply", err);
                this.toastError("Failed to delete reply");
            } finally {
                this.$set(reply, "deleting", false);
            }
        },
        async submitThreadReply(comment) {
            const text = (comment.replyDraft || "").trim();
            if (!text || !this.expandedPr) return;

            this.$set(comment, "submittingReply", true);
            try {
                const resp = await this.$http.post("/Azure/ReplyPullRequestThread", {
                    repositoryId: this.expandedPr.repositoryId,
                    pullRequestId: this.expandedPr.id,
                    threadId: comment.threadId || 1,
                    text
                });
                const data = resp?.data;
                if (data && Array.isArray(data.replies)) {
                    this.$set(comment, "replies", data.replies.map((r, rIdx) => this.decorateReplyItem(r, rIdx)));
                } else {
                    const replies = Array.isArray(comment.replies) ? [...comment.replies] : [];
                    replies.push(this.decorateReplyItem({
                        commentId: Date.now(),
                        authorName: this.userName || "Alex Morgan",
                        publishedDate: new Date().toISOString(),
                        text
                    }, replies.length));
                    this.$set(comment, "replies", replies);
                }
                this.$set(comment, "replyDraft", "");
                this.$set(comment, "replyFocused", false);
                this.syncPrCommentGroupsFromThreads();
                this.toastSuccess("Reply posted", `PR !${this.expandedPr.id}`);
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                console.error("Failed to post PR thread reply", err);
                this.toastError("Failed to post reply");
            } finally {
                this.$set(comment, "submittingReply", false);
            }
        },
        async changeThreadStatus(comment, action) {
            if (!comment || !action || !this.expandedPr) return;

            this.$set(comment, "updatingStatus", true);
            try {
                const resp = await this.$http.post("/Azure/UpdatePullRequestThreadStatus", {
                    repositoryId: this.expandedPr.repositoryId,
                    pullRequestId: this.expandedPr.id,
                    threadId: comment.threadId || 1,
                    status: action.targetStatus
                });
                const updatedStatus = resp?.data?.status || action.targetStatus;
                const isActive = updatedStatus === "Active";
                this.$set(comment, "status", updatedStatus);
                this.$set(comment, "isActive", isActive);
                if (resp?.data?.allowedTransitions) {
                    this.$set(comment, "allowedTransitions", resp.data.allowedTransitions);
                } else {
                    this.$set(comment, "allowedTransitions", []);
                }
                this.syncPrCommentGroupsFromThreads();
                this.toastSuccess("Thread status updated", `${this.commentStatusLabel(updatedStatus)} · PR !${this.expandedPr.id}`);
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                console.error("Failed to update PR thread status", err);
                this.toastError("Failed to update thread status");
            } finally {
                this.$set(comment, "updatingStatus", false);
            }
        },
        syncPrCommentGroupsFromThreads() {
            if (!this.expandedPr) return;
            const threads = (this.allPrComments && this.allPrComments.length)
                ? this.allPrComments
                : this.expandedComments;
            const countsByStatus = {};
            threads.forEach(t => {
                const st = t.status || "Active";
                countsByStatus[st] = (countsByStatus[st] || 0) + 1;
            });
            const newGroups = Object.keys(countsByStatus).map(st => ({
                status: st,
                isActive: st === "Active",
                count: countsByStatus[st]
            })).sort((a, b) => Number(b.isActive) - Number(a.isActive));

            this.$set(this.expandedPr, "commentGroups", newGroups);

            const hasActive = newGroups.some(g => g.isActive && g.count > 0);
            if (!hasActive) {
                const reasons = (this.expandedPr.blockerReasons || []).filter(
                    r => !String(r).toLowerCase().includes("comment")
                );
                this.$set(this.expandedPr, "blockerReasons", reasons);
                if (reasons.length === 0 && !this.expandedPr.hasConflicts) {
                    this.$set(this.expandedPr, "isBlocked", false);
                    this.$set(this.expandedPr, "qualityGateStatus", "Passed");
                }
            } else {
                const reasons = [...(this.expandedPr.blockerReasons || [])];
                if (!reasons.some(r => String(r).toLowerCase().includes("comment"))) {
                    reasons.push("Active comment threads");
                }
                this.$set(this.expandedPr, "blockerReasons", reasons);
                this.$set(this.expandedPr, "isBlocked", true);
                this.$set(this.expandedPr, "qualityGateStatus", "Blocked");
            }
        },
        visibleReviewers(pr) {
            const reviewers = pr.reviewers || [];
            return reviewers;
        },
        legacyVisibleReviewers(pr) {
            const reviewers = pr.reviewers || [];
            return this.showOptionalReviewers
                ? reviewers
                : reviewers.filter(r => r.isRequired);
        },
        areArraysEqual(a, b) {
            if (!a && !b) return true;
            if (!a || !b) return false;
            const normA = Array.isArray(a) ? (a.length > 1 && a.every(c => typeof c === 'string' && c.length === 1) ? [a.join('')] : a) : (a ? [a] : []);
            const normB = Array.isArray(b) ? (b.length > 1 && b.every(c => typeof c === 'string' && c.length === 1) ? [b.join('')] : b) : (b ? [b] : []);
            if (normA.length !== normB.length) return false;
            const sortedA = [...normA].sort();
            const sortedB = [...normB].sort();


            return sortedA.every((v, i) => v === sortedB[i]);
        },
        isTitleDirty(pr) {
            return (pr.titleDraft || "") !== (pr.title || "");
        },
        resetTitle(pr) {
            pr.titleDraft = pr.title;
        },
        async saveTitle(pr) {
            if (!this.isTitleDirty(pr))
                return;

            this.$set(pr, "savingTitle", true);
            try {
                const resp = await this.$http.post("/Azure/UpdatePullRequestTitle", {
                    repositoryId: pr.repositoryId,
                    pullRequestId: pr.id,
                    title: pr.titleDraft
                });
                if (resp.data) {
                    const idx = this.pullRequests.findIndex(p => p.id === pr.id);
                    if (idx !== -1) {
                        const merged = Object.assign({}, this.pullRequests[idx], resp.data);
                        merged.titleDraft = merged.title;
                        this.$set(this.pullRequests, idx, merged);
                    } else {
                        Object.assign(pr, resp.data);
                        pr.titleDraft = pr.title;
                    }
                } else {
                    pr.title = pr.titleDraft;
                }
                this.toastSuccess("Title updated", `PR !${pr.id}`);
            } catch (err) {
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                if (err?.response?.data?.isPermissionError) return;
                console.error("Failed to update PR title", err);
                const msg = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null) || err?.message || "Failed to update PR title";
                const lower = (msg || '').toLowerCase();
                if (err?.response?.status === 403 || lower.includes('permission') || lower.includes('authorized') || lower.includes('contribute') || lower.includes('vs30063')) {
                    this.toastPermissionRequired({
                        actionName: 'Update Pull Request Title',
                        requiredScope: 'Code (read & write)',
                        requiredScopeKey: 'vso.code_write',
                        requiredPermission: 'Contribute to pull requests',
                        explanation: msg || "Updating pull request titles requires the 'Code (read & write)' PAT scope in Azure DevOps.",
                        guidance: "In Azure DevOps: User Settings (top right) → Personal Access Tokens → Edit token → Ensure 'Code (read & write)' is checked."
                    });
                    return;
                }
                this.toastError("Failed to update PR title", msg);
            } finally {
                this.$set(pr, "savingTitle", false);
            }
        },
        statusLabel(pr) {
            if (pr.status === "Abandoned" || pr.status === "Completed") return pr.status;
            if (pr.isDraft) return "Draft";
            return pr.status;
        },
        effectiveStatus(pr) {
            if (pr.status === "Abandoned" || pr.status === "Completed") return pr.status;
            return pr.isDraft ? "Draft" : pr.status;
        },
        statusVariant(pr) {
            switch (pr.status) {
                case "Completed": return "success";
                case "Abandoned": return "danger";
            }
            if (pr.isDraft) return "secondary";
            switch (pr.status) {
                case "Active": return "primary";
                default: return "light";
            }
        },
        canActivate(pr) {
            return (pr.isDraft || pr.status === "Abandoned") && pr.status !== "Completed";
        },
        canDraft(pr) {
            return !pr.isDraft && pr.status === "Active";
        },
        canComplete(pr) {
            return !pr.isDraft && pr.status === "Active";
        },
        canSetAutoComplete(pr) {
            return !pr.isDraft && pr.status === "Active" && !pr.hasAutoComplete;
        },
        canCancelAutoComplete(pr) {
            return !pr.isDraft && pr.status === "Active" && !!pr.hasAutoComplete;
        },
        canAbandon(pr) {
            return pr.status !== "Completed" && pr.status !== "Abandoned";
        },
        prActionButtons(pr) {
            if (this.isViewOnlyMode) {
                return [];
            }
            if (this.isTitleDirty(pr)) {
                return [
                    { key: "saveTitle", icon: "bi bi-save", variant: "warning", title: "Save Title", disabled: pr.savingTitle },
                    { key: "resetTitle", icon: "bi bi-arrow-counterclockwise", variant: "primary", title: "Reset Changes", disabled: pr.savingTitle }
                ];
            }
            return [
                { key: "complete", icon: "bi bi-check2-circle", variant: "success", title: pr.hasConflicts ? "Cannot Complete (Merge Conflicts)" : "Complete Pull Request", visible: this.canComplete(pr), disabled: this.isAnyRowModifying || pr.hasConflicts },
                { key: "setAutoComplete", icon: "bi bi-lightning-charge-fill", variant: "primary", title: "Set Auto-Complete", visible: this.canSetAutoComplete(pr), disabled: this.isAnyRowModifying },
                { key: "cancelAutoComplete", icon: "bi bi-lightning-charge", variant: "warning", title: "Cancel Auto-Complete", visible: this.canCancelAutoComplete(pr), disabled: this.isAnyRowModifying },
                { key: "draft", icon: "bi bi-pencil", variant: "secondary", title: "Switch to Draft", visible: this.canDraft(pr), disabled: this.isAnyRowModifying },
                { key: "activate", icon: "bi bi-play-fill", variant: "info", title: "Activate", visible: this.canActivate(pr), disabled: this.isAnyRowModifying },
                { key: "abandon", icon: "bi bi-trash", variant: "danger", title: "Abandon", visible: this.canAbandon(pr), disabled: this.isAnyRowModifying }
            ];
        },
        onPrAction(pr, key) {
            if (this.isViewOnlyMode) {
                this.toastUpgradeRequired('Pro Plan', 'Modifying pull requests requires the Pro Plan.');
                return;
            }
            switch (key) {
                case "saveTitle": this.saveTitle(pr); break;
                case "resetTitle": this.resetTitle(pr); break;
                case "complete": this.setComplete(pr); break;
                case "setAutoComplete": this.setAutoComplete(pr, true); break;
                case "cancelAutoComplete": this.setAutoComplete(pr, false); break;
                case "draft": this.setDraft(pr); break;
                case "activate": this.setActive(pr); break;
                case "abandon": this.setAbandon(pr); break;
            }
        },
        voteVariant(vote) {
            if (vote >= 10) return "success";
            if (vote === 5) return "info";
            if (vote === -5) return "warning";
            if (vote <= -10) return "danger";
            return "light";
        },
        shortVoteStatus(reviewer) {
            if (!reviewer) return "No vote";
            const vote = Number(reviewer.vote || 0);
            if (vote === -5) return "Waiting";
            if (vote === 5) return "Suggestions";
            if (vote >= 10) return "Approved";
            if (vote <= -10) return "Rejected";
            const raw = String(reviewer.voteStatus || "No vote").trim();
            if (raw.toLowerCase() === "waiting for author") return "Waiting";
            if (raw.toLowerCase() === "approved with suggestions") return "Suggestions";
            return raw || "No vote";
        },
        reviewerVoteTooltip(reviewer) {
            if (!reviewer) return "";
            const name = this.escapeTooltipHtml(reviewer.name || "Reviewer");
            const fullStatus = this.escapeTooltipHtml(reviewer.voteStatus || "No vote");
            const reqTag = reviewer.isRequired ? "Required reviewer" : "Optional reviewer";
            return `<div class="vt-rich-tooltip"><div class="vt-rt-title"><i class="bi bi-person-badge mr-1"></i>${name}</div><div class="vt-rt-body"><div><strong>Vote:</strong> ${fullStatus}</div><div class="vt-rt-sub">${reqTag}</div></div></div>`;
        },
        getInitialsAvatarDataUri(name) {
            const clean = (name || "U").trim();
            const parts = clean.split(/[\s._-]+/).filter(Boolean);
            const initials = (parts.length >= 2
                ? `${parts[0][0]}${parts[parts.length - 1][0]}`
                : clean.slice(0, 2)).toUpperCase();
            const palette = ["#0078d4", "#0f766e", "#4f46e5", "#b45309", "#7c3aed", "#be123c"];
            let hash = 0;
            for (let i = 0; i < clean.length; i++) {
                hash = ((hash << 5) - hash + clean.charCodeAt(i)) | 0;
            }
            const bg = palette[Math.abs(hash) % palette.length];
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><circle cx="24" cy="24" r="24" fill="${bg}"/><text x="50%" y="52%" dominant-baseline="central" text-anchor="middle" fill="#ffffff" font-family="system-ui,-apple-system,Segoe UI,Roboto,sans-serif" font-size="18" font-weight="700">${initials}</text></svg>`;
            return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
        },
        getReviewerAvatarSrc(reviewer) {
            const src = (reviewer && reviewer.source) ? String(reviewer.source).trim() : "";
            if (src && !src.includes("PCFET0NU") && !src.includes("PGh0bWw") && !src.includes("DQo8IURPQ1RZUEU") && !src.includes("Cg8IURPQ1RZUEU")) {
                return src;
            }
            return this.getInitialsAvatarDataUri(reviewer?.name);
        },
        onReviewerImageError(e, reviewer) {
            const target = e && e.target;
            if (!target) return;
            const fallback = this.getInitialsAvatarDataUri(reviewer?.name);
            if (target.src !== fallback) {
                target.src = fallback;
            }
        },
        _unusedLegacyReviewerAvatarFallback() {
            return;
        },
        branchVariant(branch) {
            const name = (branch || "").toLowerCase();
            if (name.startsWith("dev")) return "success";
            if (name === "master" || name === "main") return "warning";
            return "light";
        },
        toggleCollapseAll() {
            const anyOpen = this.collapsesStates.some(s => !s);
            this.collapsesStates = this.visibleGroups.map(() => anyOpen);
        },
        toggleCollapseSingle(index) {
            this.$set(this.collapsesStates, index, !this.collapsesStates[index]);
        },
        setDraft(pr) {
            this.updatePrStatus(pr, { isDraft: true }, "Switched to Draft");
        },
        setActive(pr) {
            this.updatePrStatus(pr, { isDraft: false, status: "Active" }, "Activated");
        },
        setComplete(pr) {
            this.updatePrStatus(pr, { status: "Completed", isDraft: false, autoComplete: false }, "Completed");
        },
        setAutoComplete(pr, enabled) {
            this.updatePrStatus(pr, { autoComplete: enabled }, enabled ? "Auto-Complete Enabled" : "Auto-Complete Cancelled");
        },
        setAbandon(pr) {
            this.updatePrStatus(pr, { status: "Abandoned" }, "Abandoned");
        },
        canSwitchTargetBranch(pr) {
            if (this.isViewOnlyMode || !pr) return false;
            return pr.status !== "Completed" && pr.status !== "Abandoned";
        },
        isSpecialBranchName(name) {
            const lower = (name || "").trim().toLowerCase();
            if (!lower) return false;
            return lower === "main"
                || lower === "master"
                || lower === "dev"
                || lower === "develop"
                || lower === "staging"
                || lower === "stage"
                || lower === "prod"
                || lower === "production"
                || lower.startsWith("release")
                || lower.startsWith("hotfix");
        },
        isPrimaryCompareBranchName(name) {
            const lower = (name || "").trim().toLowerCase();
            return lower === "main" || lower === "master" || lower === "dev" || lower === "develop" || lower === "trunk";
        },
        specialBranchSortRank(name, isDefault, isCompare) {
            const lower = (name || "").trim().toLowerCase();
            if (isDefault) return 0;
            if (isCompare) return 1;
            if (lower === "main") return 2;
            if (lower === "master") return 3;
            if (lower === "dev" || lower === "develop") return 4;
            if (lower === "staging" || lower === "stage") return 5;
            if (lower.startsWith("release")) return 6;
            if (lower.startsWith("hotfix")) return 7;
            if (lower === "prod" || lower === "production") return 8;
            return 10;
        },
        getUiZoomScale() {
            if (typeof document !== "undefined" && document.documentElement) {
                const z = parseFloat(document.documentElement.style.zoom);
                if (Number.isFinite(z) && z > 0) return z;
            }
            return 1;
        },
        updateTargetBranchOverlayPosition(prId, fallbackEl) {
            const id = prId || this.editingTargetBranchPrId;
            if (!id) return;
            const refKey = `targetBranchGroup-${id}`;
            const refVal = this.$refs[refKey];
            const groupEl = (Array.isArray(refVal) ? refVal[0] : refVal) || fallbackEl;
            if (!groupEl || typeof groupEl.getBoundingClientRect !== "function") return;
            const zoomScale = this.getUiZoomScale();
            const rect = groupEl.getBoundingClientRect();
            const viewportW = (window.innerWidth || 1024) / zoomScale;
            const viewportH = (window.innerHeight || 768) / zoomScale;
            const maxLeft = Math.max(8, viewportW - 240);
            let left = Math.min(Math.max(8, Math.round((rect.left / zoomScale) * 10) / 10), maxLeft);
            let top = Math.round((rect.top / zoomScale - 2) * 10) / 10;
            const spaceBelow = viewportH - (rect.bottom / zoomScale);
            this.targetBranchDropUp = spaceBelow < 220 && (rect.top / zoomScale) > 240;
            const editorEl = this.$el && this.$el.querySelector('[data-test="editor-target-branch"]');
            if (editorEl && typeof editorEl.getBoundingClientRect === "function") {
                editorEl.style.position = "fixed";
                editorEl.style.left = `${left}px`;
                editorEl.style.top = `${top}px`;
                const editorRect = editorEl.getBoundingClientRect();
                const targetRectLeft = rect.left;
                const targetRectTop = rect.top - 2 * zoomScale;
                const errX = targetRectLeft - editorRect.left;
                const errY = targetRectTop - editorRect.top;
                if (Math.abs(errX) > 0.5 || Math.abs(errY) > 0.5) {
                    const effScaleX = (left > 0 && editorRect.left > 0) ? (editorRect.left / left) : zoomScale;
                    const effScaleY = (top > 0 && editorRect.top > 0) ? (editorRect.top / top) : zoomScale;
                    left = Math.min(Math.max(8, Math.round((left + errX / effScaleX) * 10) / 10), maxLeft);
                    top = Math.round((top + errY / effScaleY) * 10) / 10;
                    editorEl.style.left = `${left}px`;
                    editorEl.style.top = `${top}px`;
                }
            }
            this.targetBranchOverlayStyle = {
                position: "fixed",
                top: `${top}px`,
                left: `${left}px`,
                zIndex: 2050
            };
        },
        bindBranchPickerOutsideClick() {
            this.unbindBranchPickerOutsideClick();
            this._onBranchPickerDocClick = (e) => {
                if (!this.editingTargetBranchPrId) return;
                const editor = this.$el && this.$el.querySelector('[data-test="editor-target-branch"]');
                if (editor && editor.contains(e.target)) return;
                this.closeTargetBranchPicker();
            };
            this._onBranchPickerReposition = (e) => {
                if (!this.editingTargetBranchPrId) return;
                const editor = this.$el && this.$el.querySelector('[data-test="editor-target-branch"]');
                if (e && e.type === "scroll" && editor && editor.contains(e.target)) return;
                this.updateTargetBranchOverlayPosition();
            };
            document.addEventListener("mousedown", this._onBranchPickerDocClick, true);
            window.addEventListener("scroll", this._onBranchPickerReposition, true);
            window.addEventListener("resize", this._onBranchPickerReposition, { passive: true });
        },
        unbindBranchPickerOutsideClick() {
            if (this._onBranchPickerDocClick) {
                document.removeEventListener("mousedown", this._onBranchPickerDocClick, true);
                this._onBranchPickerDocClick = null;
            }
            if (this._onBranchPickerReposition) {
                window.removeEventListener("scroll", this._onBranchPickerReposition, true);
                window.removeEventListener("resize", this._onBranchPickerReposition);
                this._onBranchPickerReposition = null;
            }
        },
        openTargetBranchPicker(pr, event) {
            if (!this.canSwitchTargetBranch(pr)) return;
            const fallbackEl = event?.currentTarget?.closest?.(".pr-target-branch-group") || event?.currentTarget || null;
            this.editingTargetBranchPrId = pr.id;
            this.targetBranchSearch = "";
            this.targetBranchHighlightIdx = 0;
            this.updateTargetBranchOverlayPosition(pr.id, fallbackEl);
            this.bindBranchPickerOutsideClick();
            this.$nextTick(() => {
                this.updateTargetBranchOverlayPosition(pr.id, fallbackEl);
                const refKey = `targetBranchInput-${pr.id}`;
                const refEl = this.$refs[refKey];
                const input = Array.isArray(refEl) ? refEl[0] : refEl;
                if (input && typeof input.focus === "function") {
                    input.focus();
                }
            });
            const repoKey = pr.repositoryId || pr.repositoryName;
            const hasCached = Boolean(repoKey && Array.isArray(this.repoBranchesCache[repoKey]));
            const hasReal = Boolean(repoKey && Array.isArray(this.repoRealBranches[repoKey]));
            this.loadRepoBranchesForPicker(pr, {
                force: !hasReal,
                background: hasCached
            });
        },
        closeTargetBranchPicker() {
            this.editingTargetBranchPrId = null;
            this.targetBranchSearch = "";
            this.targetBranchHighlightIdx = 0;
            this.targetBranchOverlayStyle = {};
            this.targetBranchDropUp = false;
            this.unbindBranchPickerOutsideClick();
        },
        loadStoredRepoBranchesCache() {
            try {
                const raw = localStorage.getItem(PR_REPO_BRANCHES_CACHE_KEY);
                if (!raw) return {};
                const parsed = JSON.parse(raw);
                if (!parsed || typeof parsed !== "object") return {};
                const now = Date.now();
                const valid = {};
                let expiredAny = false;
                Object.keys(parsed).forEach(key => {
                    const entry = parsed[key];
                    if (entry && Array.isArray(entry.branches) && typeof entry.cachedAt === "number" && (now - entry.cachedAt) < PR_REPO_BRANCHES_CACHE_TTL_MS) {
                        valid[key] = entry.branches;
                    } else {
                        expiredAny = true;
                    }
                });
                if (expiredAny) {
                    const nextStore = {};
                    Object.keys(valid).forEach(k => {
                        nextStore[k] = {
                            branches: valid[k],
                            cachedAt: parsed[k]?.cachedAt || now
                        };
                    });
                    localStorage.setItem(PR_REPO_BRANCHES_CACHE_KEY, JSON.stringify(nextStore));
                }
                return valid;
            } catch (e) {
                return {};
            }
        },
        persistRepoBranchesToCache(repoKeys, incomingBranches) {
            const validKeys = (repoKeys || []).filter(Boolean).map(k => String(k));
            if (!validKeys.length) return;
            const incomingDefaultEntry = (incomingBranches || []).find(b => Boolean(b?.isDefault));
            const incomingDefaultLower = incomingDefaultEntry ? (incomingDefaultEntry.name || "").trim().toLowerCase() : null;
            const mergedMap = new Map();
            const upsertCached = (b, isFromExisting = false) => {
                const cleanName = (b?.name || "").trim();
                if (!cleanName) return;
                const lower = cleanName.toLowerCase();
                const isDefault = incomingDefaultLower
                    ? (lower === incomingDefaultLower)
                    : Boolean(b.isDefault);
                const isCompare = !isDefault && (Boolean(b.isCompare) || this.isPrimaryCompareBranchName(cleanName));
                const isSpecial = Boolean(b.isSpecial) || isDefault || isCompare || this.isSpecialBranchName(cleanName);
                const isPicked = Boolean(b.isPicked);
                if (!isDefault && !isCompare && !isSpecial && !isPicked) return;
                const prev = mergedMap.get(lower);
                const nextIsDefault = incomingDefaultLower
                    ? (lower === incomingDefaultLower)
                    : Boolean(isDefault || (!isFromExisting && prev && prev.isDefault) || (isFromExisting && b.isDefault));
                const nextIsCompare = !nextIsDefault && (Boolean(isCompare || (prev && prev.isCompare)) || this.isPrimaryCompareBranchName(cleanName));
                mergedMap.set(lower, {
                    name: prev ? prev.name : cleanName,
                    isDefault: nextIsDefault,
                    isCompare: nextIsCompare,
                    isSpecial: Boolean(isSpecial || (prev && prev.isSpecial)),
                    isPicked: Boolean(isPicked || (prev && prev.isPicked)),
                    specialType: nextIsDefault ? "default" : (nextIsCompare ? "compare" : (b.specialType || (prev && prev.specialType) || ""))
                });
            };
            validKeys.forEach(k => {
                const existing = this.repoBranchesCache[k];
                if (Array.isArray(existing)) {
                    existing.forEach(b => upsertCached(b, true));
                }
            });
            (incomingBranches || []).forEach(b => upsertCached(b, false));
            let seenDefault = false;
            const finalCached = Array.from(mergedMap.values()).map(item => {
                const isDef = Boolean(item.isDefault) && !seenDefault;
                if (isDef) seenDefault = true;
                const isComp = !isDef && (Boolean(item.isCompare) || this.isPrimaryCompareBranchName(item.name));
                return {
                    ...item,
                    isDefault: isDef,
                    isCompare: isComp,
                    specialType: isDef ? "default" : (isComp ? "compare" : (item.specialType === "default" ? "" : (item.specialType || "")))
                };
            });
            validKeys.forEach(k => {
                this.$set(this.repoBranchesCache, k, finalCached);
            });
            try {
                const raw = localStorage.getItem(PR_REPO_BRANCHES_CACHE_KEY);
                const parsed = (raw && JSON.parse(raw)) || {};
                const now = Date.now();
                validKeys.forEach(k => {
                    parsed[k] = {
                        branches: finalCached,
                        cachedAt: now
                    };
                });
                localStorage.setItem(PR_REPO_BRANCHES_CACHE_KEY, JSON.stringify(parsed));
            } catch (e) {
                // ignore storage quota errors
            }
        },
        addPickedBranchToRepoCache(pr, branchName) {
            const clean = (branchName || "").trim();
            if (!pr || !clean) return;
            const repoKey = pr.repositoryId || pr.repositoryName;
            const realList = (repoKey && this.repoRealBranches[repoKey]) || (pr.repositoryName && this.repoRealBranches[pr.repositoryName]) || [];
            const cachedList = (repoKey && this.repoBranchesCache[repoKey]) || (pr.repositoryName && this.repoBranchesCache[pr.repositoryName]) || [];
            const lower = clean.toLowerCase();
            const matched = realList.find(b => (b?.name || "").toLowerCase() === lower)
                || cachedList.find(b => (b?.name || "").toLowerCase() === lower);
            const isDefault = Boolean(matched?.isDefault);
            const isCompare = !isDefault && (Boolean(matched?.isCompare) || this.isPrimaryCompareBranchName(clean));
            const isSpecial = Boolean(matched?.isSpecial) || isDefault || isCompare || this.isSpecialBranchName(clean);
            this.persistRepoBranchesToCache([pr.repositoryId, pr.repositoryName], [{
                name: matched?.name || clean,
                isDefault,
                isCompare,
                isSpecial,
                isPicked: true
            }]);
        },
        onTargetBranchSearchInput(pr) {
            this.targetBranchHighlightIdx = 0;
            if ((this.targetBranchSearch || "").trim().length > 0) {
                this.loadRepoBranchesForPicker(pr, { loadReal: true });
            }
        },
        warmPrRepositoriesBranchCache(prs) {
            if (!Array.isArray(prs) || !prs.length) return;
            const seenRepos = new Set();
            const uncachedSamplePrs = [];
            prs.forEach(pr => {
                const repoKey = pr.repositoryId || pr.repositoryName;
                if (!repoKey || seenRepos.has(repoKey)) return;
                seenRepos.add(repoKey);
                if (!Array.isArray(this.repoBranchesCache[repoKey])) {
                    uncachedSamplePrs.push(pr);
                }
            });
            uncachedSamplePrs.slice(0, 6).forEach(samplePr => {
                this.loadRepoBranchesForPicker(samplePr, { background: true });
            });
        },
        async loadRepoBranchesForPicker(pr, options = {}) {
            if (!pr) return;
            const repoKey = pr.repositoryId || pr.repositoryName;
            if (!options.force && repoKey) {
                if (options.loadReal) {
                    if (Array.isArray(this.repoRealBranches[repoKey])) return;
                } else if (Array.isArray(this.repoBranchesCache[repoKey])) {
                    return;
                }
            }
            if (!this._inflightRepoBranchLoads) {
                this._inflightRepoBranchLoads = {};
            }
            if (repoKey && this._inflightRepoBranchLoads[repoKey]) {
                return this._inflightRepoBranchLoads[repoKey];
            }
            if (!options.background) {
                this.loadingTargetBranches = true;
            }
            const loadPromise = (async () => {
                try {
                    const baseParams = {
                        authors: "all",
                        specials: "default,main,master,dev,develop,release,staging,hotfix,prod,production"
                    };
                    if (pr.repositoryId) {
                        baseParams.repositoryId = pr.repositoryId;
                    }
                    if (this.activeProject?.id) {
                        baseParams.projectId = this.activeProject.id;
                    }
                    let resp;
                    try {
                        resp = await this.$http.get("/Azure/ListBranches", {
                            params: { ...baseParams, allTime: true }
                        });
                    } catch (innerErr) {
                        if (innerErr?.status === 402 || innerErr?.response?.status === 402 || innerErr?.isPlanUpgrade) {
                            resp = await this.$http.get("/Azure/ListBranches", {
                                params: { ...baseParams, allTime: false }
                            });
                        } else {
                            throw innerErr;
                        }
                    }
                    const items = Array.isArray(resp?.data) ? resp.data : [];
                    const byRepo = new Map();
                    const addBranchToRepoBucket = (rId, rName, b) => {
                        const cleanName = (b?.name || "").trim();
                        if (!cleanName) return;
                        const bucketKey = rId || rName || repoKey;
                        if (!bucketKey) return;
                        if (!byRepo.has(bucketKey)) {
                            byRepo.set(bucketKey, { keys: new Set(), map: new Map() });
                        }
                        const bucket = byRepo.get(bucketKey);
                        if (rId) bucket.keys.add(String(rId));
                        if (rName) bucket.keys.add(String(rName));
                        const lower = cleanName.toLowerCase();
                        const isDefault = Boolean(b.isDefault);
                        const isCompare = !isDefault && (Boolean(b.isCompare) || this.isPrimaryCompareBranchName(cleanName));
                        const isSpecial = Boolean(b.isSpecial) || isDefault || isCompare || this.isSpecialBranchName(cleanName);
                        bucket.map.set(lower, {
                            name: cleanName,
                            isDefault,
                            isCompare,
                            isSpecial,
                            specialType: isDefault ? "default" : (isCompare ? "compare" : (b.specialType || ""))
                        });
                    };

                    items.forEach(entry => {
                        if (Array.isArray(entry?.branches)) {
                            entry.branches.forEach(b => addBranchToRepoBucket(entry.repositoryId, entry.repositoryName, b));
                        } else if (entry?.name) {
                            addBranchToRepoBucket(entry.repositoryId || pr.repositoryId, entry.repositoryName || pr.repositoryName, entry);
                        }
                    });

                    if (byRepo.size > 0) {
                        byRepo.forEach(bucket => {
                            const allReal = Array.from(bucket.map.values());
                            const keys = Array.from(bucket.keys);
                            keys.forEach(k => {
                                this.$set(this.repoRealBranches, k, allReal);
                            });
                            const specialsOnly = allReal.filter(b => b.isDefault || b.isCompare || b.isSpecial);
                            this.persistRepoBranchesToCache(keys, specialsOnly);
                        });
                    } else if (repoKey) {
                        [pr.repositoryId, pr.repositoryName].filter(Boolean).forEach(k => {
                            this.$set(this.repoRealBranches, String(k), []);
                        });
                        this.persistRepoBranchesToCache([pr.repositoryId, pr.repositoryName], []);
                    }
                } catch (err) {
                    console.warn("Could not load repository branches for picker", err);
                } finally {
                    if (repoKey && this._inflightRepoBranchLoads) {
                        delete this._inflightRepoBranchLoads[repoKey];
                    }
                    if (!options.background) {
                        this.loadingTargetBranches = false;
                    }
                }
            })();
            if (repoKey) {
                this._inflightRepoBranchLoads[repoKey] = loadPromise;
            }
            return loadPromise;
        },
        getTargetBranchSections(pr) {
            if (!pr) return [];
            const repoKey = pr.repositoryId || pr.repositoryName;
            const cached = (repoKey && this.repoBranchesCache[repoKey]) || (pr.repositoryName && this.repoBranchesCache[pr.repositoryName]) || [];
            const realBranches = (repoKey && this.repoRealBranches[repoKey]) || (pr.repositoryName && this.repoRealBranches[pr.repositoryName]) || [];
            const query = (this.targetBranchSearch || "").trim();
            const queryLower = query.toLowerCase();
            const map = new Map();

            const authoritativeDefaultEntry = realBranches.find(b => Boolean(b?.isDefault))
                || cached.find(b => Boolean(b?.isDefault));
            const authoritativeDefaultKey = authoritativeDefaultEntry
                ? (authoritativeDefaultEntry.name || "").trim().toLowerCase()
                : null;

            const upsertBranch = (name, meta = {}) => {
                const clean = (name || "").trim();
                if (!clean) return;
                if (clean.toLowerCase() === (pr.sourceBranch || "").trim().toLowerCase()) return;
                const key = clean.toLowerCase();
                const existing = map.get(key);
                const isDefault = authoritativeDefaultKey
                    ? (key === authoritativeDefaultKey)
                    : Boolean(meta.isDefault || (existing && existing.isDefault));
                const isCompare = !isDefault && (
                    Boolean(meta.isCompare || (existing && existing.isCompare))
                    || this.isPrimaryCompareBranchName(clean)
                );
                const isSpecial = Boolean(meta.isSpecial || isDefault || isCompare || (existing && existing.isSpecial) || this.isSpecialBranchName(clean));
                const isPicked = Boolean(meta.isPicked || (existing && existing.isPicked));
                const badge = isDefault
                    ? "Default"
                    : (isCompare ? "Compare" : (isSpecial ? "Special" : (isPicked ? "Cached" : (meta.badge || ""))));
                const badgeType = isDefault
                    ? "default"
                    : (isCompare ? "compare" : (isSpecial ? "special" : "cached"));
                map.set(key, {
                    name: existing ? existing.name : clean,
                    isDefault,
                    isCompare,
                    isSpecial,
                    isPicked,
                    isCurrent: clean.toLowerCase() === (pr.targetBranch || "").trim().toLowerCase(),
                    badge,
                    badgeType
                });
            };

            cached.forEach(b => upsertBranch(b.name, b));

            if (pr.targetBranch) {
                upsertBranch(pr.targetBranch, { isPicked: true });
            }

            if (queryLower) {
                realBranches.forEach(b => upsertBranch(b.name, b));
            }

            let allItems = Array.from(map.values());
            if (queryLower) {
                allItems = allItems.filter(item => item.name.toLowerCase().includes(queryLower));
            }

            const specialItems = allItems
                .filter(i => i.isSpecial)
                .sort((a, b) => {
                    const rankDiff = this.specialBranchSortRank(a.name, a.isDefault, a.isCompare) - this.specialBranchSortRank(b.name, b.isDefault, b.isCompare);
                    return rankDiff !== 0 ? rankDiff : a.name.localeCompare(b.name);
                });

            const otherItems = allItems
                .filter(i => !i.isSpecial)
                .sort((a, b) => a.name.localeCompare(b.name));

            let flatIdx = 0;
            const sections = [];
            if (specialItems.length > 0) {
                sections.push({
                    key: "special",
                    label: "Special Branches",
                    icon: "bi bi-stars",
                    items: specialItems.map(item => ({ ...item, flatIndex: flatIdx++ }))
                });
            }
            if (otherItems.length > 0) {
                sections.push({
                    key: "other",
                    label: queryLower ? "Repository Branches" : "Cached Branches",
                    icon: queryLower ? "bi bi-git" : "bi bi-clock-history",
                    items: otherItems.map(item => ({ ...item, flatIndex: flatIdx++ }))
                });
            }
            return sections;
        },
        getTargetBranchFlatOptions(pr) {
            return this.getTargetBranchSections(pr).reduce((acc, s) => acc.concat(s.items), []);
        },
        moveTargetBranchHighlight(pr, delta) {
            const flat = this.getTargetBranchFlatOptions(pr);
            if (!flat.length) return;
            const len = flat.length;
            this.targetBranchHighlightIdx = (this.targetBranchHighlightIdx + delta + len) % len;
        },
        onTargetBranchEnter(pr) {
            const flat = this.getTargetBranchFlatOptions(pr);
            if (flat.length > 0) {
                const chosen = flat[this.targetBranchHighlightIdx] || flat[0];
                if (chosen) {
                    this.selectTargetBranch(pr, chosen.name);
                }
            }
        },
        selectTargetBranch(pr, branchName) {
            const target = (branchName || "").trim();
            this.closeTargetBranchPicker();
            if (!target || !pr) return;
            this.addPickedBranchToRepoCache(pr, target);
            if (target === pr.targetBranch) return;
            if (target.toLowerCase() === (pr.sourceBranch || "").trim().toLowerCase()) {
                this.toastWarning("Invalid target branch", "Target branch cannot be the same as source branch.");
                return;
            }
            this.updatePrStatus(pr, { targetBranch: target }, `Target branch switched to ${target}`);
        },
        async updatePrStatus(pr, change, successVerb) {
            this.$set(pr, "savingTitle", true);
            const prevStatus = pr.status;
            const prevIsDraft = pr.isDraft;
            const prevHasAutoComplete = pr.hasAutoComplete;
            const prevTargetBranch = pr.targetBranch;
            if (change.optimistic && change.targetBranch !== undefined && change.targetBranch !== null) {
                this.$set(pr, "targetBranch", change.targetBranch);
            }
            if (change.optimistic && change.status !== undefined && change.status !== null) {
                pr.status = change.status;
            }
            if (change.optimistic && change.autoComplete !== undefined && change.autoComplete !== null) {
                this.$set(pr, "hasAutoComplete", change.autoComplete);
            }
            if (change.optimistic && change.isDraft !== undefined && change.isDraft !== null) {
                pr.isDraft = change.isDraft;
            }
            try {
                const resp = await this.$http.post("/Azure/UpdatePullRequestStatus", {
                    repositoryId: pr.repositoryId,
                    pullRequestId: pr.id,
                    status: change.status ?? null,
                    isDraft: change.isDraft ?? null,
                    autoComplete: change.autoComplete ?? null,
                    targetBranch: change.targetBranch ?? null
                });
                if (resp.data) {
                    const idx = this.pullRequests.findIndex(p => p.id === pr.id);
                    if (idx !== -1) {
                        const merged = Object.assign({}, this.pullRequests[idx], resp.data);
                        this.$set(this.pullRequests, idx, merged);
                    } else {
                        Object.assign(pr, resp.data);
                    }
                }
                const idxAfter = this.pullRequests.findIndex(p => p.id === pr.id);
                const targetRow = idxAfter !== -1 ? this.pullRequests[idxAfter] : pr;
                if (targetRow) {
                    const statePatch = { savingTitle: false };
                    if (change.status) {
                        statePatch.status = change.status;
                        if (change.status === "Completed" || change.status === "Abandoned") {
                            statePatch.isDraft = false;
                            statePatch.hasAutoComplete = false;
                            statePatch.isReadyToMerge = false;
                            statePatch.hasUpdatesSinceReview = false;
                            statePatch.isBlocked = false;
                        }
                    }
                    if (change.isDraft !== undefined && change.isDraft !== null) statePatch.isDraft = change.isDraft;
                    if (change.autoComplete === false) statePatch.hasAutoComplete = false;
                    if (change.targetBranch) statePatch.targetBranch = String(change.targetBranch).replace(/^refs\/heads\//i, "");
                    const updatedRow = Object.assign({}, targetRow, statePatch);
                    updatedRow.titleDraft = updatedRow.title;
                    if (idxAfter !== -1) {
                        this.$set(this.pullRequests, idxAfter, updatedRow);
                    } else {
                        Object.assign(pr, statePatch);
                    }
                }
                if (change.autoComplete === true && resp.data && !resp.data.hasAutoComplete) {
                    this.toastWarning("Auto-Complete Not Enabled", "Azure DevOps did not enable auto-complete (target branch may have no branch policies configured — use Complete instead).");
                    return;
                }
                this.toastSuccess(successVerb, `PR !${pr.id}`);
            } catch (err) {
                pr.status = prevStatus;
                pr.isDraft = prevIsDraft;
                this.$set(pr, "hasAutoComplete", prevHasAutoComplete);
                this.$set(pr, "targetBranch", prevTargetBranch);
                this.$set(pr, "savingTitle", false);
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
                console.error("Failed to update PR status", err);
                if (err?.response?.data?.isPermissionError) return;
                const msg = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : null) || err?.message || "Failed to update PR status";
                const lower = (msg || '').toLowerCase();
                if (err?.response?.status === 403 || lower.includes('permission') || lower.includes('authorized') || lower.includes('contribute') || lower.includes('vs30063')) {
                    this.toastPermissionRequired({
                        actionName: 'Update Pull Request',
                        requiredScope: 'Code (read & write)',
                        requiredScopeKey: 'vso.code_write',
                        requiredPermission: 'Contribute to pull requests',
                        explanation: msg || "Updating pull request status requires the 'Code (read & write)' PAT scope in Azure DevOps.",
                        guidance: "In Azure DevOps: User Settings (top right) → Personal Access Tokens → Edit token → Ensure 'Code (read & write)' is checked."
                    });
                    return;
                }
                this.toastError("Failed to update PR status", msg);
            } finally {
                this.$set(pr, "savingTitle", false);
            }
        }
    }
};
</script>

<style scoped>
.pr-items-grid {
    min-height: 260px;
}

.pr-items-grid .grid-row {
    align-items: center;
    min-width: 0 !important;
}

.pr-items-grid .pr-col-id {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
}

.pr-attention-pill-link {
    color: inherit;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
}

.pr-attention-pill-link:hover {
    color: inherit;
    opacity: 0.75;
}

.pr-merged-wi-btn {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.05rem 0.45rem;
    border-radius: 999px;
    border: 1px solid currentColor;
    background: transparent;
    color: inherit;
    line-height: 1.25;
    cursor: pointer;
}

.pr-merged-wi-btn:hover:not(:disabled) {
    background: rgba(16, 185, 129, 0.22);
}

.pr-merged-wi-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.pr-attention-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 3px;
}

.pr-attention-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: 0.01em;
    cursor: default;
    user-select: none;
}

.pr-attention-pill-re-review {
    background-color: rgba(245, 158, 11, 0.14);
    color: #b45309;
    border: 1px solid rgba(245, 158, 11, 0.38);
}

.pr-attention-pill-re-review.pr-attention-pill-urgent {
    background-color: rgba(245, 158, 11, 0.22);
    border-color: rgba(245, 158, 11, 0.65);
    box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.18);
}

[data-theme="dark"] .pr-attention-pill-re-review {
    background-color: rgba(245, 158, 11, 0.16);
    color: #fbbf24;
    border-color: rgba(245, 158, 11, 0.42);
}

[data-theme="dark"] .pr-attention-pill-re-review.pr-attention-pill-urgent {
    background-color: rgba(245, 158, 11, 0.24);
    color: #fde68a;
    border-color: rgba(251, 191, 36, 0.65);
}

.pr-attention-pill-ready-merge {
    background-color: rgba(16, 185, 129, 0.14);
    color: #047857;
    border: 1px solid rgba(16, 185, 129, 0.38);
}

[data-theme="dark"] .pr-attention-pill-ready-merge {
    background-color: rgba(16, 185, 129, 0.18);
    color: #34d399;
    border-color: rgba(16, 185, 129, 0.45);
}

.pr-attention-pill-auto-complete {
    background-color: rgba(14, 165, 233, 0.14);
    color: #0369a1;
    border: 1px solid rgba(14, 165, 233, 0.38);
}

[data-theme="dark"] .pr-attention-pill-auto-complete {
    background-color: rgba(56, 189, 248, 0.16);
    color: #38bdf8;
    border-color: rgba(56, 189, 248, 0.42);
}

.pr-attention-pill-conflict {
    background-color: rgba(239, 68, 68, 0.14);
    color: #b91c1c;
    border: 1px solid rgba(239, 68, 68, 0.42);
}

[data-theme="dark"] .pr-attention-pill-conflict {
    background-color: rgba(239, 68, 68, 0.18);
    color: #fca5a5;
    border-color: rgba(239, 68, 68, 0.48);
}

.pr-attention-pill-active-threads {
    background-color: rgba(245, 158, 11, 0.14);
    color: #b45309;
    border: 1px solid rgba(245, 158, 11, 0.4);
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.12s ease;
}

.pr-attention-pill-active-threads:hover {
    background-color: rgba(245, 158, 11, 0.24);
    border-color: rgba(245, 158, 11, 0.65);
    transform: translateY(-1px);
}

[data-theme="dark"] .pr-attention-pill-conflict {
    background-color: rgba(239, 68, 68, 0.14);
    color: #b91c1c;
    border: 1px solid rgba(239, 68, 68, 0.42);
}

[data-theme="dark"] .pr-attention-pill-conflict {
    background-color: rgba(239, 68, 68, 0.18);
    color: #fca5a5;
    border-color: rgba(239, 68, 68, 0.48);
}

.pr-attention-pill-active-threads {
    background-color: rgba(245, 158, 11, 0.16);
    color: #fbbf24;
    border-color: rgba(245, 158, 11, 0.42);
}

[data-theme="dark"] .pr-attention-pill-active-threads:hover {
    background-color: rgba(245, 158, 11, 0.26);
    color: #fde68a;
    border-color: rgba(251, 191, 36, 0.65);
}

.pr-items-grid .pr-col-title {
    flex-direction: column;
    align-items: stretch !important;
    justify-content: center;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
    padding-right: 0.5rem;
}

.pr-items-grid .pr-col-status {
    display: flex;
    align-items: center;
    justify-content: center;
}

.pr-items-grid .pr-col-flags {
    display: flex;
    align-items: center;
    justify-content: center;
}

.pr-items-grid .pr-col-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
}

.pr-items-grid>>>.grid-shell-scroll,
.pr-items-grid>>>.grid-shell-content {
    min-height: 260px;
}

.pr-management-page a,
.pr-management-page button,
.pr-management-page .badge {
    font-size: 0.75rem;
}

.pr-project-name {
    min-width: 220px;
    display: flex;
    align-items: center;
}

.pr-show-switch,
.pr-show-switch input,
.pr-show-switch label {
    cursor: pointer;
}

.pr-col-status .pr-show-switch {
    display: inline-flex;
    justify-content: center;
    margin-top: 2px;
}

.pr-group-select {
    margin: 4px auto 0;
    max-width: 100%;
    padding: 0 0.25rem;
    font-size: 0.7rem;
}

.pr-status-filter {
    margin-top: 2px;
    font-size: 0.7rem;
    max-width: 100%;
    position: relative;
}

.pr-status-filter>>>.dropdown-toggle {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pr-status-filter>>>.dropdown-toggle.is-pinned {
    position: fixed;
    z-index: 1050;
    width: max-content !important;
    max-width: 90vw;
    overflow: visible;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}

.pr-status-filter>>>.dropdown-menu {
    padding: 0.15rem 0 !important;
    min-width: 6rem;
}

.pr-status-filter-form {
    padding: 0.1rem 0 !important;
    min-width: 6rem;
}

.pr-status-filter-form>>>.b-dropdown-form {
    padding: 0.15rem 0.35rem !important;
}

.pr-status-filter-form>>>.custom-control {
    position: relative !important;
    display: block !important;
    padding: 0.15rem 0.4rem 0.15rem 1.1rem !important;
    margin: 0 !important;
    font-size: 0.8rem !important;
    font-weight: 400;
    line-height: 1.2;
    color: #212529;
    clear: both;
    white-space: nowrap;
    min-height: 0 !important;
}

.pr-status-filter-form>>>.custom-control:hover {
    background-color: #f8f9fa;
}

.pr-status-filter-form>>>.custom-control-label {
    width: 100%;
    cursor: pointer;
    margin-bottom: 0;
}

.pr-status-filter-form>>>.custom-control-label::before,
.pr-status-filter-form>>>.custom-control-label::after {
    top: 0.05rem;
    left: -0.95rem;
    width: 0.8rem;
    height: 0.8rem;
}

.pr-status-filter-form>>>.custom-control-input {
    left: 0.05rem;
}

.pr-reviewer {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 2px;
}

.pr-reviewer-image {
    width: 24px;
    height: 24px;
    object-fit: cover;
    flex: 0 0 auto;
}

.pr-reviewer-vote {
    white-space: nowrap;
}

.pr-reviewer-required {
    font-size: 0.65rem;
    text-transform: uppercase;
    white-space: nowrap;
}

.pr-branch {
    display: flex;
    align-items: center;
    margin-bottom: 1px;
}

.pr-col-branches {
    position: relative;
    overflow: visible !important;
}

.pr-row-editing-branch {
    position: relative;
    z-index: 40;
}

/* Deeplink-style hover tray on target branch badge */
.pr-target-branch-group {
    position: relative;
    display: inline-flex;
    align-items: stretch;
    vertical-align: middle;
}

.pr-target-branch-group.is-editing {
    visibility: hidden;
    pointer-events: none;
}

.pr-target-branch-group.can-switch .pr-target-branch-badge {
    cursor: pointer;
    transition: border-radius 0.12s ease, box-shadow 0.12s ease;
}

.pr-target-branch-group.can-switch:hover .pr-target-branch-badge,
.pr-target-branch-group.can-switch:focus-within .pr-target-branch-badge {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
}

.pr-target-branch-tray {
    display: inline-flex;
    align-items: center;
    position: absolute;
    left: calc(100% - 1px);
    top: 0;
    bottom: 0;
    z-index: 25;
    padding: 0 3px 0 2px;
    border-radius: 0 0.25rem 0.25rem 0;
    background-color: #f1f5f9;
    border: 1px solid #cbd5e1;
    border-left: none;
    box-shadow: 3px 1px 6px rgba(15, 23, 42, 0.14);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateX(-3px);
    transition: opacity 0.12s ease, transform 0.12s ease, visibility 0.12s ease;
    white-space: nowrap;
}

.pr-target-branch-group.can-switch:hover .pr-target-branch-tray,
.pr-target-branch-group.can-switch:focus-within .pr-target-branch-tray {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateX(0);
}

.pr-target-branch-pipe {
    width: 1px;
    height: 11px;
    background-color: rgba(100, 116, 139, 0.45);
    margin: 0 2px 0 1px;
    flex-shrink: 0;
}

.pr-target-branch-switch-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 17px;
    height: 15px;
    padding: 0;
    border: none;
    border-radius: 3px;
    background: transparent;
    color: #334155;
    font-size: 0.68rem;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease;
}

.pr-target-branch-switch-btn:hover {
    background-color: rgba(37, 99, 235, 0.15);
    color: #1d4ed8;
}

[data-theme="dark"] .pr-target-branch-tray {
    background-color: #1e293b;
    border-color: #475569;
    box-shadow: 3px 1px 8px rgba(0, 0, 0, 0.45);
}

[data-theme="dark"] .pr-target-branch-pipe {
    background-color: rgba(148, 163, 184, 0.4);
}

[data-theme="dark"] .pr-target-branch-switch-btn {
    color: #cbd5e1;
}

[data-theme="dark"] .pr-target-branch-switch-btn:hover {
    background-color: rgba(56, 189, 248, 0.2);
    color: #38bdf8;
}

/* Inline target branch autocomplete editor & dropdown (floating fixed overlay) */
.pr-target-branch-editor {
    position: fixed;
    display: inline-flex;
    flex-direction: column;
    width: 195px;
    z-index: 2050;
}

.pr-target-branch-input-wrap {
    display: flex;
    align-items: center;
    gap: 2px;
    background-color: #ffffff;
    padding: 1px 2px;
    border-radius: 5px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.22);
}

[data-theme="dark"] .pr-target-branch-input-wrap {
    background-color: #0f172a;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.55);
}

.pr-target-branch-input {
    height: 22px !important;
    padding: 0 6px !important;
    font-size: 0.72rem !important;
    line-height: 1.2 !important;
    border-radius: 4px !important;
}

.pr-target-branch-cancel-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 1px solid rgba(148, 163, 184, 0.35);
    border-radius: 4px;
    background: transparent;
    color: #64748b;
    font-size: 0.65rem;
    cursor: pointer;
    flex-shrink: 0;
}

.pr-target-branch-cancel-btn:hover {
    background-color: rgba(239, 68, 68, 0.12);
    color: #ef4444;
    border-color: rgba(239, 68, 68, 0.35);
}

.pr-target-branch-dropdown {
    position: absolute;
    top: calc(100% + 3px);
    left: 0;
    min-width: 210px;
    max-width: 280px;
    max-height: 230px;
    overflow-y: auto;
    background-color: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.18);
    padding: 4px 0;
    z-index: 2051;
}

.pr-target-branch-editor.open-upward .pr-target-branch-dropdown {
    top: auto;
    bottom: calc(100% + 3px);
}

[data-theme="dark"] .pr-target-branch-dropdown {
    background-color: #0f172a;
    border-color: #334155;
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.55);
}

.pr-target-branch-section-hdr {
    padding: 3px 8px 2px;
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    user-select: none;
}

[data-theme="dark"] .pr-target-branch-section-hdr {
    color: #94a3b8;
}

.pr-target-branch-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 4px 8px;
    border: none;
    background: transparent;
    text-align: left;
    font-size: 0.74rem;
    cursor: pointer;
    transition: background-color 0.1s ease;
}

.pr-target-branch-option:hover,
.pr-target-branch-option.is-highlighted {
    background-color: rgba(59, 130, 246, 0.1);
}

[data-theme="dark"] .pr-target-branch-option:hover,
[data-theme="dark"] .pr-target-branch-option.is-highlighted {
    background-color: rgba(56, 189, 248, 0.14);
}

.pr-target-branch-opt-main {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    overflow: hidden;
}

.pr-target-branch-opt-badge {
    max-width: 155px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pr-target-branch-opt-tag {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    padding: 1px 4px;
    border-radius: 3px;
    background-color: rgba(245, 158, 11, 0.15);
    color: #d97706;
    flex-shrink: 0;
}

[data-theme="dark"] .pr-target-branch-opt-tag {
    background-color: rgba(251, 191, 36, 0.16);
    color: #fbbf24;
}

.pr-target-branch-opt-tag.is-compare {
    background-color: rgba(14, 165, 233, 0.15);
    color: #0284c7;
}

[data-theme="dark"] .pr-target-branch-opt-tag.is-compare {
    background-color: rgba(56, 189, 248, 0.18);
    color: #38bdf8;
}

.pr-target-branch-opt-tag.is-cached {
    background-color: rgba(100, 116, 139, 0.16);
    color: #64748b;
}

[data-theme="dark"] .pr-target-branch-opt-tag.is-cached {
    background-color: rgba(148, 163, 184, 0.18);
    color: #94a3b8;
}

.pr-target-branch-loading,
.pr-target-branch-empty {
    padding: 6px 10px;
    font-size: 0.72rem;
    color: #64748b;
}

.pr-branch-repo {
    margin-bottom: 3px;
}

.pr-empty {
    display: inline-block;
    color: #ced4da;
    opacity: 0.7;
}

.status-abandoned {
    background-color: #d67730 !important;
    color: #ffffff !important;
}

.pr-blocker-icon,
.pr-passed-icon,
.pr-pending-icon {
    font-size: 0.95rem;
    vertical-align: middle;
    cursor: pointer;
}

.pr-comments {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 6px;
    line-height: 1;
}

.pr-comment-group {
    display: inline-flex;
    align-items: center;
    font-size: 0.85rem;
    text-decoration: none;
    line-height: 1;
}

.pr-comment-group:hover {
    text-decoration: none;
}

.pr-comment-count {
    font-size: 0.7rem;
    font-weight: 700;
    margin-left: 2px;
}

.pr-comment-line {
    display: flex;
    align-items: flex-start;
    padding: 8px 0;
    border-bottom: 1px solid var(--vt-border-color, rgba(128, 128, 128, 0.2));
    font-size: 0.82rem;
}

.pr-comment-line:last-child {
    border-bottom: none;
}

[data-theme="dark"] .pr-comment-line {
    border-bottom-color: rgba(255, 255, 255, 0.12);
}

.pr-comment-status {
    flex: 0 0 auto;
    font-size: 0.65rem;
    margin-top: 1px;
}

.pr-comment-text {
    word-break: break-word;
    line-height: 1.35;
}

.pr-comment-text code {
    background-color: var(--vt-badge-bg, rgba(255, 255, 255, 0.08));
    color: var(--vt-accent-color, #d63384);
    padding: 0 3px;
    border-radius: 3px;
    font-size: 0.85em;
}

[data-theme="dark"] .pr-comment-text code {
    background-color: rgba(255, 255, 255, 0.08);
    color: #f472b6;
}

.pr-comment-text a {
    word-break: break-all;
}

.pr-col-id {
    position: relative;
    overflow: visible;
    display: flex;
    align-items: center;
}

.pr-workitem-line {
    display: flex;
    align-items: center;
    margin-bottom: 3px;
    position: relative;
    overflow: visible;
}

.pr-workitem-btn {
    margin: 0 6px 0 0;
    flex: 0 0 auto;
    align-self: flex-start;
}

.pr-workitem-title {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.2;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.pr-item-row {
    border-top: 1px solid rgba(148, 163, 184, 0.2);
    padding-top: 6px;
    padding-bottom: 6px;
}

.pr-item-row.pr-row-clamped>.pr-col-workitems,
.pr-item-row.pr-row-clamped>.pr-col-reviewers,
.pr-item-row.pr-row-clamped>.pr-col-branches {
    max-height: 92px;
    overflow: hidden;
}

.pr-item-row:not(.pr-row-clamped)>.pr-col-workitems,
.pr-item-row:not(.pr-row-clamped)>.pr-col-reviewers,
.pr-item-row:not(.pr-row-clamped)>.pr-col-branches {
    max-height: 320px;
    overflow-y: auto;
}

.pr-item-wrap {
    position: relative;
}

.pr-expander {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 6px;
    margin-top: -4px;
    color: transparent;
    cursor: pointer;
    border-radius: 0 0 4px 4px;
    background-color: rgba(148, 163, 184, 0.2);
    transition: height 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}

.pr-expander>i {
    font-size: 0.75rem;
    line-height: 1;
}

.pr-expander:hover {
    height: 18px;
    color: #64748b;
    background-color: rgba(148, 163, 184, 0.35);
}

.filter-toolbar-divider {
    height: 22px;
    width: 1px;
    background-color: rgba(148, 163, 184, 0.3);
    align-self: center;
}

.pr-grid-overlay {
    min-height: 260px;
    width: 100%;
    display: block;
}

.pr-grid-overlay.no-sprints {
    margin-top: 1.25rem;
}
.filter-options-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.compact-checkbox {
    font-size: 0.82rem;
    line-height: 1.3;
    margin-bottom: 0 !important;
    width: 100%;
}
.compact-checkbox >>> .custom-control-label {
    cursor: pointer;
    font-size: 0.82rem;
    padding-top: 1px;
    user-select: none;
    white-space: nowrap;
    width: 100%;
}
.compact-checkbox >>> .custom-control-input:disabled ~ .custom-control-label {
    cursor: not-allowed;
    opacity: 0.65;
}

/* Mobile & Tablet Structured Card Layout (<=991.98px) */
@media (max-width: 991.98px) {
    .mobile-col-caption {
        display: block !important;
        font-size: 0.65rem !important;
        font-weight: 700 !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
        color: #64748b !important;
        margin-bottom: 0.25rem !important;
        line-height: 1 !important;
        white-space: nowrap !important;
        user-select: none;
    }

    [data-theme="dark"] .mobile-col-caption {
        color: #94a3b8 !important;
    }

    /* Flatten group outer card so individual items become clean structured cards */
    .pr-items-grid .grid-shell-content > div > .card {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        margin: 0 !important;
    }

    .pr-items-grid .grid-shell-content > div > .card > .card-body {
        padding: 0 !important;
    }

    .pr-project-row {
        background-color: var(--vt-surface-card, rgba(30, 41, 59, 0.35));
        border: 1px solid var(--vt-border, rgba(148, 163, 184, 0.2));
        border-radius: 0.5rem;
        padding: 0.5rem 0.75rem !important;
        margin-bottom: 0.5rem !important;
    }

    /* PR Item Structured Card */
    .pr-item-row {
        display: flex !important;
        flex-wrap: wrap !important;
        align-items: flex-start !important;
        max-height: none !important;
        overflow: visible !important;
        padding: 0.75rem 0.85rem !important;
        margin-bottom: 0.75rem !important;
        gap: 0.45rem 0.65rem !important;
        border-radius: 0.65rem !important;
        background-color: var(--vt-surface-card, rgba(30, 41, 59, 0.45));
        border: 1px solid var(--vt-border, rgba(148, 163, 184, 0.22)) !important;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    [data-theme="dark"] .pr-item-row {
        background-color: rgba(30, 41, 59, 0.65) !important;
        border-color: rgba(148, 163, 184, 0.24) !important;
    }

    [data-theme="light"] .pr-item-row {
        background-color: #ffffff !important;
        border-color: rgba(203, 213, 225, 0.85) !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }

    .pr-col-collapser,
    .pr-expander {
        display: none !important;
    }

    /* Row 1: ID + Status + Flags on left, Action buttons on right */
    .pr-col-id,
    .pr-col-status,
    .pr-col-flags {
        order: 1 !important;
        flex: 0 0 auto !important;
        width: auto !important;
        max-width: none !important;
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
    }

    .pr-col-buttons {
        order: 1 !important;
        margin-left: auto !important;
        flex: 0 0 auto !important;
        width: auto !important;
        max-width: none !important;
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
    }

    /* Row 2: Full-width Title */
    .pr-items-grid .pr-item-row > .pr-col-title,
    .pr-col-title {
        order: 2 !important;
        flex: 0 0 100% !important;
        width: 100% !important;
        max-width: 100% !important;
        padding: 0 !important;
        margin-top: 0.2rem !important;
        margin-bottom: 0.35rem !important;
        text-align: left !important;
    }

    .pr-col-title ::v-deep textarea {
        font-size: 0.95rem !important;
        font-weight: 600 !important;
        line-height: 1.35 !important;
    }

    /* Row 3: Branches & Reviewers */
    .pr-col-branches {
        order: 3 !important;
        flex: 1 1 200px !important;
        max-height: none !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
    }

    .pr-col-reviewers {
        order: 3 !important;
        flex: 1 1 140px !important;
        max-height: none !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
    }

    /* Row 4: Work Items */
    .pr-col-workitems {
        order: 4 !important;
        flex: 0 0 100% !important;
        width: 100% !important;
        max-height: none !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
        margin-top: 0.25rem !important;
    }
}

.pr-mobile-noop {
    opacity: 0.65;
}
</style>

<style>
/* Non-scoped: modal is teleported to body, so scoped styles won't reach it. */
.pr-comments-modal .modal-dialog {
    max-width: min(860px, 92vw);
    width: 760px;
    min-width: 340px;
}

.pr-comments-modal .modal-body,
.pr-comments-modal .pr-comments-modal-body {
    min-height: 220px;
}

.pr-comments-modal .pr-comments-overlay {
    min-height: 200px;
}

.pr-comments-modal .pr-comment-thread {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 10px 12px;
    margin-bottom: 8px;
    border: 1px solid rgba(148, 163, 184, 0.25);
    border-radius: 8px;
    background-color: rgba(248, 250, 252, 0.65);
    font-size: 0.84rem;
}

[data-theme="dark"] .pr-comments-modal .pr-comment-thread {
    border-color: rgba(148, 163, 184, 0.2);
    background-color: rgba(15, 23, 42, 0.55);
}

.pr-comments-modal .pr-thread-header {
    gap: 6px;
}

.pr-comments-modal .pr-comment-author {
    font-size: 0.8rem;
    color: var(--vt-text, #1e293b);
}

[data-theme="dark"] .pr-comments-modal .pr-comment-author {
    color: #e2e8f0;
}

.pr-comments-modal .pr-thread-action-btn {
    font-size: 0.72rem;
    padding: 0.12rem 0.48rem;
    line-height: 1.25;
    font-weight: 600;
}

.pr-comments-modal .pr-comment-text {
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.4;
}

.pr-comments-modal .pr-thread-replies {
    padding-left: 12px;
    border-left: 2px solid rgba(56, 189, 248, 0.45);
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.pr-comments-modal .pr-thread-reply-item {
    padding: 6px 8px;
    border-radius: 6px;
    background-color: rgba(148, 163, 184, 0.1);
    font-size: 0.8rem;
}

[data-theme="dark"] .pr-comments-modal .pr-thread-reply-item {
    background-color: rgba(30, 41, 59, 0.75);
}

.pr-comments-modal .pr-thread-reply-meta {
    font-size: 0.74rem;
    margin-bottom: 2px;
}

.pr-comments-modal .pr-reply-action-btn {
    font-size: 0.72rem;
    line-height: 1.1;
    text-decoration: none !important;
}

.pr-comments-modal .pr-reply-action-btn:hover {
    text-decoration: underline !important;
}

/* Rich HTML tooltip styling for PR badges, blockers, and reviewer votes */
.tooltip-inner .vt-rich-tooltip {
    text-align: left;
    max-width: 280px;
    line-height: 1.38;
    padding: 2px 1px;
}

.tooltip-inner .vt-rich-tooltip .vt-rt-title {
    font-weight: 700;
    font-size: 0.78rem;
    margin-bottom: 3px;
    padding-bottom: 3px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.16);
    display: flex;
    align-items: center;
}

.tooltip-inner .vt-rich-tooltip .vt-rt-body {
    font-size: 0.74rem;
    font-weight: 400;
    opacity: 0.95;
}

.tooltip-inner .vt-rich-tooltip .vt-rt-list {
    margin: 3px 0 0;
    padding-left: 15px;
}

.tooltip-inner .vt-rich-tooltip .vt-rt-list li {
    margin-bottom: 2px;
}

.tooltip-inner .vt-rich-tooltip .vt-rt-sub,
.tooltip-inner .vt-rich-tooltip .vt-rt-tag {
    font-size: 0.7rem;
    opacity: 0.82;
}
</style>
